import React, { useState, useEffect } from "react";
import useFontFamilies from "../../hooks/useFontFamilies";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { MuiColorInput } from "mui-color-input";
import UseToast from "../UseToast";
import "./BrandingSettings.css";
import ImageIcon from "@mui/icons-material/Image";
import PreviewIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Avatar,
  Grid,
  Button,
  Alert,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Slider,
  FormControlLabel,
  Switch,
  Card,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  AccessTime,
  ArrowBack,
  Palette,
  Settings,
  Save,
  Delete,
  Psychology,
} from "@mui/icons-material";
import { usePxToVw } from "../../hooks/usePxToVw";
import useS3Upload from "../../hooks/useS3Upload";
import PreviewModal from "./PreviewModal";
import MainWayfinderMedia from "./MainWayfinderMedia";
import ToggleSwitch from "../backend/ToggleSwitch";
import StepByStepDirectionsConfig from "./StepByStepDirectionsConfig";
import EnhancedTextDirectionConfig from "./EnhancedTextDirectionConfig";

const BrandingSettings = ({ userId, buttonColor }) => {
  const fontOptions = useFontFamilies('option');
  const { showMsgSuccessToast, showErrorToast } = UseToast();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { pxToVw } = usePxToVw();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState("");

  // Language state
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [availableLanguages, setAvailableLanguages] = useState(['en']);

  // Add this state with your other useState declarations
  const [selectedBackButtonFile, setSelectedBackButtonFile] = useState(null);
  const [selectedCenterLogoFile, setSelectedCenterLogoFile] = useState(null);
  const [selectedLogoBeforeMapFile, setSelectedLogoBeforeMapFile] = useState(null);

  // QR Code Generator States
  const [selectedQrPattern, setSelectedQrPattern] = useState(1);
  const [qrGenerating, setQrGenerating] = useState(false);
  const [qrGenerationProgress, setQrGenerationProgress] = useState(0);
  const [qrGenerationMessage, setQrGenerationMessage] = useState('');
  const [qrLogo, setQrLogo] = useState(null);
  const [qrText, setQrText] = useState(''); // Add QR text input for search parameter
  
  // Direct Map Link QR Generation States
  const [generatingDirectMapQR, setGeneratingDirectMapQR] = useState(false);
  const [directMapQRProgress, setDirectMapQRProgress] = useState(0);
  const [directMapQRStatus, setDirectMapQRStatus] = useState('');
  
  // Step-by-Step Directions State
  const [stepByStepConfig, setStepByStepConfig] = useState({
    codeInsert1: '<break time="1.5s" />',
    opening: 'Let me help you find your destination.',
    mapTimeDelay: 0,
    codeInsert2: '<break time="1.0s" />',
    stepByStepOpening: 'To get to the <Location Name> first turn <Right / Left>',
    stepByStepDirections: '<Directions from map>',
    codeInsert3: '<break time="0.5s" />',
    closing: 'Have a nice day.',
    codeInsert4: '<break time="0.5s" />',
    globalQrClosing: {
      enabled: true,
      text: 'Scan the QR Code to take these directions with you.',
    },
    codeInsert5: '<break time="3.5s" />',
  });
  
  const [qrSettings, setQrSettings] = useState({
    frame: {
      frameStyle: 'none',
      frameColor: '#000000',
      frameText: 'SCAN ME',
    },
    eyes: {
      eyeFrameShape: 'square',
      eyeFrameColor: '#000000',
      eyeBallShape: 'square',
      eyeBallColor: '#000000',
    },
    patterns: {
      dataPattern: 'square',
      colorDark: '#000000',
    },
    background: {
      backgroundColor: 'rgba(255,255,255,0)',
    },
    logoImage: null,
  });

  // QR Code Configuration States
  const [qrCodeConfig, setQrCodeConfig] = useState({
    enabled: true,
    autoGenerate: true,
    enableDirectMapLinkQr: false, // Add this field
    globalSettings: {
      frame: {
        frameStyle: 'none',
        frameColor: '#000000',
        frameText: 'SCAN ME',
      },
      eyes: {
        eyeFrameShape: 'square',
        eyeFrameColor: '#000000',
        eyeBallShape: 'square',
        eyeBallColor: '#000000',
      },
      patterns: {
        dataPattern: 'square',
        colorDark: '#000000',
      },
      background: {
        backgroundColor: 'rgba(255,255,255,0)',
      },
      logoImage: null,
    },
  });

  // Keyboard state management
  const [searchTerm, setSearchTerm] = useState("");
  const [keyboardMode, setKeyboardMode] = useState("lowercase");

  // State for MainWayfinderMedia integration
  const [uploadedUrls, setUploadedUrls] = useState({
    upperLeft: {
      fileUrl: "",
      fileName: "",
      enabled: false,
      width: 100,
    },
    upperRight: {
      fileUrl: "",
      fileName: "",
      enabled: false,
      width: 100,
    },
    bottomLeft: {
      fileUrl: "",
      fileName: "",
      enabled: false,
      width: 100,
    },
    bottomRight: {
      fileUrl: "",
      fileName: "",
      enabled: false,
      width: 100,
    },
  });

  const [uploadingStates, setUploadingStates] = useState({
    upperLeft: false,
    upperRight: false,
    bottomLeft: false,
    bottomRight: false,
  });

  const { uploadFile, uploading } = useS3Upload({
    onSuccess: (fileUrl, file) => {
      showMsgSuccessToast("Image uploaded successfully!");
      // Handle upload based on current context
      if (uploadingLogoPosition) {
        handleLogoUploadSuccess(uploadingLogoPosition, fileUrl, file.name);
      } else if (selectedBackButtonFile) {
        // Only update back button if we're uploading a back button image
        updateBackButton("buttonImage", {
          ...settings.backButton.buttonImage,
          fileUrl: fileUrl,
          fileName: file.name,
        });
      } else if (selectedCenterLogoFile) {
        // Update center logo
        updateSearchBox("centerLogo", {
          ...settings.searchBox.centerLogo,
          fileUrl: fileUrl,
          fileName: file.name,
          enabled: true,
        });
      } else if (selectedLogoBeforeMapFile) {
        // Update logo before map
        updateLogoBeforeMap("fileUrl", fileUrl);
        updateLogoBeforeMap("fileName", file.name);
        updateLogoBeforeMap("enabled", true);
      }
    },
    onError: (error) => {
      showErrorToast("Failed to upload image");
      console.error("Upload error:", error);
    },
  });

  // State for logo upload tracking
  const [uploadingLogoPosition, setUploadingLogoPosition] = useState(null);

  const [settings, setSettings] = useState({
    searchBox: {
      enabled: true,
      headerText: "Search",
      headerFontFamily: "Arial",
      headerColor: "#000000",
      headerFontSize: 16,
      headerFontWeight: 600,
      placeholder: "Search...",
      backgroundColor: "#ffffff",
      textColor: "#000000",
      fontSize: 24,
      opacity: 1,
      padding: "12",
      width: "300",
      headerPositionTop: "10",
      searchInputPositionTop: "50",
      buttonSpacing: "20",
      buttonMarginTop: "40",
      keyboardSpacing: "40",
      keyboardPositionBottom: "25",
      wheelchairContentOffsetY: 20, // Wheelchair mode content offset percentage
      border: {
        width: "1",
        color: "#cccccc",
        radius: "8",
      },
      boxShadow: {
        shadowAngle: 45,
        shadowSize: 5,
        shadowDistance: 2,
        shadowAlpha: 1,
        shadowColor: "#000000",
      },
      keyboard: {
        backgroundColor: "#ffffff",
        borderWidth: "1",
        borderColor: "#e0e0e0",
        borderRadius: "12",
        keyBackgroundColor: "#ffffff",
        keyBorderWidth: "1",
        keyBorderColor: "#e0e0e0",
        keyBorderRadius: "8",
        keyTextColor: "#322727",
        shadow: {
          shadowAngle: 45,
          shadowSize: 5,
          shadowDistance: 2,
          shadowAlpha: 0.15,
          shadowColor: "#000000",
        },
      },
      centerLogo: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: "100",
        positionTop: "10",
      },
    },
    timeoutPopup: {
      enabled: true,
      popupPadding: "24",
      timeoutDuration: 30,
      text: {
        content: "Are you still there?",
        color: "#000000",
        fontSize: 20,
        fontWeight: 600,
      },
      popupWidth: "500",
      popupHeight: "300",
      fontFamily: "Arial",
      button: {
        text: "Continue",
        textColor: "#ffffff",
        backgroundColor: "#000000",
        borderRadius: "8",
        padding: "40",
        width: "125",
        height: "60",
        fontSize: "16",
        shadow: {
          shadowAngle: 45,
          shadowSize: 5,
          shadowDistance: 2,
          shadowAlpha: 1,
          shadowColor: "#000000",
        },
      },
      avatarScreen: {
        headerText: "What would you like to do next?",
        countdownDuration: 30,
        replayButton: {
          text: "Replay Directions",
          textColor: "#ffffff",
          backgroundColor: "#000000",
          borderRadius: "8",
          padding: "20",
          fontSize: "16",
          shadow: {
            shadowAngle: 45,
            shadowSize: 5,
            shadowDistance: 2,
            shadowAlpha: 1,
            shadowColor: "#000000",
          },
        },
        backToSearchButton: {
          text: "Back to Search",
          textColor: "#ffffff",
          backgroundColor: "#666666",
          borderRadius: "8",
          padding: "20",
          fontSize: "16",
          shadow: {
            shadowAngle: 45,
            shadowSize: 5,
            shadowDistance: 2,
            shadowAlpha: 1,
            shadowColor: "#000000",
          },
        },
        closeWindowButton: {
          text: "Close this Window",
          textColor: "#ffffff",
          backgroundColor: "#999999",
          borderRadius: "8",
          padding: "20",
          fontSize: "16",
          shadow: {
            shadowAngle: 45,
            shadowSize: 5,
            shadowDistance: 2,
            shadowAlpha: 1,
            shadowColor: "#000000",
          },
        },
      },
    },
    backButton: {
      enabled: true,
      custom: true,
      buttonImage: {
        fileUrl: "",
        fileName: "",
        width: "50",
        height: "50",
      },
      text: "Back",
      textColor: "#ffffff",
      backgroundColor: "#000000",
      opacity: 0.9,
      angle: 0,
      letterSpacing: 5,
      fontSize: 20,
      fontWeight: 500,
      position: {
        top: "20",
        left: "20",
      },
      borderWidth: "1",
      borderColor: "#000000",
      width: "125",
      height: "50",
      borderRadius: {
        topLeft: "8",
        topRight: "8",
        bottomLeft: "8",
        bottomRight: "8",
      },
      paddingTopBottom: "20",
      paddingLeftRight: "24",
      shadow: {
        shadowAngle: 45,
        shadowSize: 5,
        shadowDistance: 2,
        shadowAlpha: 1,
        shadowColor: "#000000",
      },
    },
    qrCode: {
      widthPx: 403,
      heightPx: 326,
      top: null,
      bottom: 8,
      left: null,
      right: 3,
      backgroundColor: "#127E10",
      headerText: "Scan Me",
      headerFontSize: 14,
      headerColor: "#127E10",
      footerText: "Get directions on your phone",
      footerFontSize: 10,
      footerColor: "#666666",
      showFrame: true,
      backgroundTransparent: false,
    },
    mapPosition: {
      widthPx: 537,
      heightPx: 1080,
      top: 10,
      right: 3,
      left: null,
      bottom: null,
      boxShadow: {
        shadowAngle: 45,
        shadowSize: 5,
        shadowDistance: 2,
        shadowAlpha: 0.3,
        shadowColor: "#000000",
      },
      borderColor: "#000000",
      borderWidth: "1",
      borderRadius: "8",
    },
    logoBeforeMap: {
      enabled: false,
      fileUrl: "",
      fileName: "",
      width: 200,
      displayDuration: 3,
    },
  });

  // Get language name from code
  const getLanguageName = (code) => {
    const languageNames = {
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      ru: 'Russian',
      ja: 'Japanese',
      ko: 'Korean',
      zh: 'Chinese',
      ar: 'Arabic',
      hi: 'Hindi',
      bn: 'Bengali',
      pa: 'Punjabi',
      te: 'Telugu',
      mr: 'Marathi',
      ta: 'Tamil',
      ur: 'Urdu',
      gu: 'Gujarati',
      kn: 'Kannada',
      ml: 'Malayalam',
      th: 'Thai',
      vi: 'Vietnamese',
      tr: 'Turkish',
      pl: 'Polish',
      uk: 'Ukrainian',
      ro: 'Romanian',
      nl: 'Dutch',
      el: 'Greek',
      cs: 'Czech',
      sv: 'Swedish',
      hu: 'Hungarian',
      fi: 'Finnish',
      no: 'Norwegian',
      da: 'Danish',
      he: 'Hebrew',
      id: 'Indonesian',
      ms: 'Malay',
      fa: 'Persian',
    };
    return languageNames[code] || code.toUpperCase();
  };

  // Helper function to get language flag
  const getLanguageFlag = (code) => {
    const flagMap = {
      en: "us",
      es: "es",
      fr: "fr",
      de: "de",
      it: "it",
      pt: "pt",
      ru: "ru",
      zh: "cn",
      ja: "jp",
      ko: "kr",
      ar: "sa",
      hi: "in",
      tr: "tr",
      nl: "nl",
      sv: "se",
      pl: "pl",
      th: "th",
      vi: "vn",
      id: "id",
    };
    return flagMap[code] || "xx";
  };

  // Function to change language
  const handleLanguageChange = (langCode, showToast = false) => {
    // Before changing language, we need to update the text fields for the new language
    // Get the original multilingual objects
    const originalHeaderText = settings.qrCode._originalHeaderText || {};
    const originalFooterText = settings.qrCode._originalFooterText || {};
    const originalPlaceholder = settings.searchBox._originalPlaceholder || {};
    const originalTimeoutContent = settings.timeoutPopup.text._originalContent || {};
    
    // Avatar Screen multilanguage fields
    const originalAvatarHeaderText = settings.timeoutPopup.avatarScreen._originalHeaderText || {};
    const originalReplayButtonText = settings.timeoutPopup.avatarScreen.replayButton._originalText || {};
    const originalBackToSearchButtonText = settings.timeoutPopup.avatarScreen.backToSearchButton._originalText || {};
    const originalCloseWindowButtonText = settings.timeoutPopup.avatarScreen.closeWindowButton._originalText || {};
    
    // Extract text for the new language
    const newHeaderText = originalHeaderText[langCode] || originalHeaderText['en'] || 'Scan Me';
    const newFooterText = originalFooterText[langCode] || originalFooterText['en'] || 'Get directions on your phone';
    const newPlaceholder = originalPlaceholder[langCode] || originalPlaceholder['en'] || 'Search...';
    const newTimeoutContent = originalTimeoutContent[langCode] || originalTimeoutContent['en'] || 'Are you still there?';
    
    // Avatar Screen text for new language
    const newAvatarHeaderText = originalAvatarHeaderText[langCode] || originalAvatarHeaderText['en'] || 'What would you like to do next?';
    const newReplayButtonText = originalReplayButtonText[langCode] || originalReplayButtonText['en'] || 'Replay Directions';
    const newBackToSearchButtonText = originalBackToSearchButtonText[langCode] || originalBackToSearchButtonText['en'] || 'Back to Search';
    const newCloseWindowButtonText = originalCloseWindowButtonText[langCode] || originalCloseWindowButtonText['en'] || 'Close this Window';
    
    // Update settings with new language text
    setSettings(prev => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        placeholder: newPlaceholder,
        _originalPlaceholder: originalPlaceholder,
      },
      qrCode: {
        ...prev.qrCode,
        headerText: newHeaderText,
        footerText: newFooterText,
        _originalHeaderText: originalHeaderText,
        _originalFooterText: originalFooterText,
      },
      timeoutPopup: {
        ...prev.timeoutPopup,
        text: {
          ...prev.timeoutPopup.text,
          content: newTimeoutContent,
          _originalContent: originalTimeoutContent,
        },
        avatarScreen: {
          ...prev.timeoutPopup.avatarScreen,
          headerText: newAvatarHeaderText,
          _originalHeaderText: originalAvatarHeaderText,
          replayButton: {
            ...prev.timeoutPopup.avatarScreen.replayButton,
            text: newReplayButtonText,
            _originalText: originalReplayButtonText,
          },
          backToSearchButton: {
            ...prev.timeoutPopup.avatarScreen.backToSearchButton,
            text: newBackToSearchButtonText,
            _originalText: originalBackToSearchButtonText,
          },
          closeWindowButton: {
            ...prev.timeoutPopup.avatarScreen.closeWindowButton,
            text: newCloseWindowButtonText,
            _originalText: originalCloseWindowButtonText,
          },
        }
      }
    }));
    
    setCurrentLanguage(langCode);
    
    // Save preference to localStorage
    localStorage.setItem(`language_pref_${userId}`, langCode);
    
    // Show success message only if explicitly requested
    if (showToast) {
      showMsgSuccessToast(`Language changed to ${getLanguageName(langCode)}`);
    }
  };

  // Helper function to safely get timeout popup content as string
  const getTimeoutPopupContent = () => {
    const content = settings.timeoutPopup.text.content;
    // If it's already a string, return it
    if (typeof content === 'string') {
      return content;
    }
    // If it's an object (Map), extract current language
    if (typeof content === 'object' && content !== null) {
      return content[currentLanguage] || content['en'] || 'Are you still there?';
    }
    // Fallback
    return 'Are you still there?';
  };

  // ==================== UTILITY FUNCTIONS ====================

  // Keyboard helper functions
  const performSearch = (value) => {
    // This is a preview component, so we don't actually perform search
    // Just update the search term for display purposes
    console.log("Search term:", value);
  };

  const handleKeyboardEnter = (value) => {
    // Handle enter key press in preview
    console.log("Enter pressed with value:", value);
  };

  const hexToRgba = (hex, alpha = 1) => {
    let c = hex.replace("#", "");
    if (c.length === 3) {
      c = c
        .split("")
        .map((x) => x + x)
        .join("");
    }
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const degToRad = (deg) => deg * (Math.PI / 180);

  const getOffset = (angle, distance) => {
    const rad = degToRad(angle);
    const x = Math.cos(rad) * distance;
    const y = Math.sin(rad) * distance;
    return { x, y };
  };

  const getBoxShadow = ({
    shadowAngle,
    shadowSize,
    shadowDistance,
    shadowAlpha,
    shadowColor,
  }) => {
    const { x, y } = getOffset(shadowAngle, shadowDistance);
    const rgbaColor = hexToRgba(shadowColor, shadowAlpha);
    const blur = shadowSize * 2;
    const spread = shadowSize * 0.5;
    return `${x}px ${y}px ${blur}px ${spread}px ${rgbaColor}`;
  };

  const fetchAvailableLanguages = async () => {
    try {
      const response = await axios.get(`/api/language/${userId}`);
      if (response.data.success) {
        const languages = response.data.languages || ['en'];
        setAvailableLanguages(languages);
        
        // Try to get saved language preference from localStorage
        const savedLang = localStorage.getItem(`language_pref_${userId}`);
        if (savedLang && languages.includes(savedLang)) {
          setCurrentLanguage(savedLang);
        } else {
          // Default to first language or 'en'
          setCurrentLanguage(languages[0] || 'en');
        }
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      setAvailableLanguages(['en']);
      setCurrentLanguage('en');
    }
  };

  // Fetch Step-by-Step Directions Configuration
  const fetchStepByStepConfig = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/step-by-step-directions/global/${userId}`);
      if (response.data.success) {
        setStepByStepConfig(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching step-by-step directions:", error);
    }
  };

  // Save Step-by-Step Directions Configuration
  const saveStepByStepConfig = async () => {
    try {
      await axios.post(`/api/wayfinder/step-by-step-directions/global/${userId}`, stepByStepConfig);
      showMsgSuccessToast("Step-by-step directions configuration saved successfully!");
    } catch (error) {
      console.error("Error saving step-by-step directions:", error);
      showErrorToast("Failed to save step-by-step directions configuration");
    }
  };



  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `/api/wayfinder/branding-settings/${userId}`,
      );
      if (response.data.success) {
        // Merge with defaults to ensure all fields exist
        const fetchedSettings = response.data.data;

 
        // Convert languages Map to object if it exists
        let searchBoxSettings = { ...fetchedSettings.searchBox };
        if (searchBoxSettings.languages) {
          // Backend sends Map as object, ensure it's properly handled
          const languagesObj = searchBoxSettings.languages;
          
          // Extract current language content
          const currentLangContent = languagesObj[currentLanguage] || languagesObj['en'];
          
          if (currentLangContent) {
            console.log(`Loading content for language: ${currentLanguage}`, currentLangContent);
            
            // Update uploadedUrls from current language
            setUploadedUrls(currentLangContent.uploadedUrls || {
              upperLeft: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
              upperRight: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
              bottomLeft: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
              bottomRight: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
            });
            
            // Update searchBox with current language content
            searchBoxSettings = {
              ...searchBoxSettings,
              headerText: currentLangContent.headerText || "Search",
              centerLogo: currentLangContent.centerLogo || {
                fileUrl: "",
                fileName: "",
                enabled: false,
                width: "100",
                positionTop: "10",
              },
            };
          }
        }

        // Handle placeholder - extract current language text if it's an object
        const originalPlaceholder = searchBoxSettings.placeholder;
        if (searchBoxSettings.placeholder && typeof searchBoxSettings.placeholder === 'object') {
          searchBoxSettings.placeholder = searchBoxSettings.placeholder[currentLanguage] || searchBoxSettings.placeholder['en'] || 'Search...';
          // Store original for saving later
          searchBoxSettings._originalPlaceholder = originalPlaceholder;
        } else if (typeof searchBoxSettings.placeholder === 'string') {
          // Already a string, keep it
          searchBoxSettings._originalPlaceholder = { [currentLanguage]: searchBoxSettings.placeholder };
        } else {
          // No placeholder, set default
          searchBoxSettings.placeholder = 'Search...';
          searchBoxSettings._originalPlaceholder = { [currentLanguage]: 'Search...' };
        }

        // Handle QR Code text fields - extract current language text if it's an object
        let qrCodeSettings = { ...fetchedSettings.qrCode };
        
        // Store original multilingual objects for later use
        const originalHeaderText = qrCodeSettings.headerText;
        const originalFooterText = qrCodeSettings.footerText;
        
        if (qrCodeSettings.headerText && typeof qrCodeSettings.headerText === 'object') {
          qrCodeSettings.headerText = qrCodeSettings.headerText[currentLanguage] || qrCodeSettings.headerText['en'] || 'Scan Me';
          // Store original for saving later
          qrCodeSettings._originalHeaderText = originalHeaderText;
        }
        if (qrCodeSettings.footerText && typeof qrCodeSettings.footerText === 'object') {
          qrCodeSettings.footerText = qrCodeSettings.footerText[currentLanguage] || qrCodeSettings.footerText['en'] || 'Get directions on your phone';
          // Store original for saving later
          qrCodeSettings._originalFooterText = originalFooterText;
        }

        // Handle Timeout Popup text - extract current language text if it's an object
        let timeoutPopupSettings = { ...fetchedSettings.timeoutPopup };
        
        // Store original multilingual object for later use
        const originalTimeoutContent = timeoutPopupSettings.text?.content;
        
        if (timeoutPopupSettings.text?.content && typeof timeoutPopupSettings.text.content === 'object') {
          timeoutPopupSettings.text.content = timeoutPopupSettings.text.content[currentLanguage] || timeoutPopupSettings.text.content['en'] || 'Are you still there?';
          // Store original for saving later
          timeoutPopupSettings.text._originalContent = originalTimeoutContent;
        } else if (typeof timeoutPopupSettings.text?.content === 'string') {
          // Already a string, keep it
          timeoutPopupSettings.text._originalContent = { [currentLanguage]: timeoutPopupSettings.text.content };
        } else {
          // No content, set default
          if (!timeoutPopupSettings.text) timeoutPopupSettings.text = {};
          timeoutPopupSettings.text.content = 'Are you still there?';
          timeoutPopupSettings.text._originalContent = { [currentLanguage]: 'Are you still there?' };
        }

        // Handle Avatar Screen multilanguage fields
        if (timeoutPopupSettings.avatarScreen) {
          // Handle headerText
          const originalAvatarHeaderText = timeoutPopupSettings.avatarScreen.headerText;
          if (timeoutPopupSettings.avatarScreen.headerText && typeof timeoutPopupSettings.avatarScreen.headerText === 'object') {
            timeoutPopupSettings.avatarScreen.headerText = timeoutPopupSettings.avatarScreen.headerText[currentLanguage] || timeoutPopupSettings.avatarScreen.headerText['en'] || 'What would you like to do next?';
            timeoutPopupSettings.avatarScreen._originalHeaderText = originalAvatarHeaderText;
          } else if (typeof timeoutPopupSettings.avatarScreen.headerText === 'string') {
            timeoutPopupSettings.avatarScreen._originalHeaderText = { [currentLanguage]: timeoutPopupSettings.avatarScreen.headerText };
          } else {
            timeoutPopupSettings.avatarScreen.headerText = 'What would you like to do next?';
            timeoutPopupSettings.avatarScreen._originalHeaderText = { [currentLanguage]: 'What would you like to do next?' };
          }

          // Handle replayButton.text
          if (timeoutPopupSettings.avatarScreen.replayButton) {
            const originalReplayButtonText = timeoutPopupSettings.avatarScreen.replayButton.text;
            if (timeoutPopupSettings.avatarScreen.replayButton.text && typeof timeoutPopupSettings.avatarScreen.replayButton.text === 'object') {
              timeoutPopupSettings.avatarScreen.replayButton.text = timeoutPopupSettings.avatarScreen.replayButton.text[currentLanguage] || timeoutPopupSettings.avatarScreen.replayButton.text['en'] || 'Replay Directions';
              timeoutPopupSettings.avatarScreen.replayButton._originalText = originalReplayButtonText;
            } else if (typeof timeoutPopupSettings.avatarScreen.replayButton.text === 'string') {
              timeoutPopupSettings.avatarScreen.replayButton._originalText = { [currentLanguage]: timeoutPopupSettings.avatarScreen.replayButton.text };
            } else {
              timeoutPopupSettings.avatarScreen.replayButton.text = 'Replay Directions';
              timeoutPopupSettings.avatarScreen.replayButton._originalText = { [currentLanguage]: 'Replay Directions' };
            }
          }

          // Handle backToSearchButton.text
          if (timeoutPopupSettings.avatarScreen.backToSearchButton) {
            const originalBackToSearchButtonText = timeoutPopupSettings.avatarScreen.backToSearchButton.text;
            if (timeoutPopupSettings.avatarScreen.backToSearchButton.text && typeof timeoutPopupSettings.avatarScreen.backToSearchButton.text === 'object') {
              timeoutPopupSettings.avatarScreen.backToSearchButton.text = timeoutPopupSettings.avatarScreen.backToSearchButton.text[currentLanguage] || timeoutPopupSettings.avatarScreen.backToSearchButton.text['en'] || 'Back to Search';
              timeoutPopupSettings.avatarScreen.backToSearchButton._originalText = originalBackToSearchButtonText;
            } else if (typeof timeoutPopupSettings.avatarScreen.backToSearchButton.text === 'string') {
              timeoutPopupSettings.avatarScreen.backToSearchButton._originalText = { [currentLanguage]: timeoutPopupSettings.avatarScreen.backToSearchButton.text };
            } else {
              timeoutPopupSettings.avatarScreen.backToSearchButton.text = 'Back to Search';
              timeoutPopupSettings.avatarScreen.backToSearchButton._originalText = { [currentLanguage]: 'Back to Search' };
            }
          }

          // Handle closeWindowButton.text
          if (timeoutPopupSettings.avatarScreen.closeWindowButton) {
            const originalCloseWindowButtonText = timeoutPopupSettings.avatarScreen.closeWindowButton.text;
            if (timeoutPopupSettings.avatarScreen.closeWindowButton.text && typeof timeoutPopupSettings.avatarScreen.closeWindowButton.text === 'object') {
              timeoutPopupSettings.avatarScreen.closeWindowButton.text = timeoutPopupSettings.avatarScreen.closeWindowButton.text[currentLanguage] || timeoutPopupSettings.avatarScreen.closeWindowButton.text['en'] || 'Close this Window';
              timeoutPopupSettings.avatarScreen.closeWindowButton._originalText = originalCloseWindowButtonText;
            } else if (typeof timeoutPopupSettings.avatarScreen.closeWindowButton.text === 'string') {
              timeoutPopupSettings.avatarScreen.closeWindowButton._originalText = { [currentLanguage]: timeoutPopupSettings.avatarScreen.closeWindowButton.text };
            } else {
              timeoutPopupSettings.avatarScreen.closeWindowButton.text = 'Close this Window';
              timeoutPopupSettings.avatarScreen.closeWindowButton._originalText = { [currentLanguage]: 'Close this Window' };
            }
          }
        }

        setSettings({
          searchBox: {
            ...settings.searchBox,
           ...searchBoxSettings,
          },
          timeoutPopup: {
            ...settings.timeoutPopup,
            ...timeoutPopupSettings,
          },
          backButton: {
            ...settings.backButton,
            ...fetchedSettings.backButton,
          },
          qrCode: {
            ...settings.qrCode,
            ...qrCodeSettings,
          },
          mapPosition: {
            ...settings.mapPosition,
            ...fetchedSettings.mapPosition,
          },
          logoBeforeMap: {
            ...settings.logoBeforeMap,
            ...fetchedSettings.logoBeforeMap,
          },
        });

        // If there are logos in the fetched settings, update uploadedUrls
        if (fetchedSettings.searchBox?.logos) {
          setUploadedUrls(fetchedSettings.searchBox.logos);
        }
      }

      // Fetch QR Code Configuration
      const qrConfigResponse = await axios.get(`/api/wayfinder/qr-config/${userId}`);
      if (qrConfigResponse.data.success) {
        const fetchedQrConfig = qrConfigResponse.data.data;
        
        console.log('=== Fetched QR Config DEBUG ===');
        console.log('Fetched qrCodeConfig:', JSON.stringify(fetchedQrConfig, null, 2));
        console.log('enableDirectMapLinkQr from backend:', fetchedQrConfig.enableDirectMapLinkQr);
        
        setQrCodeConfig(fetchedQrConfig);
        setQrSettings(fetchedQrConfig.globalSettings);
        
        // Also update the main settings state with QR config
        setSettings(prevSettings => ({
          ...prevSettings,
          qrCodeConfig: fetchedQrConfig,
          qrCode: {
            ...prevSettings.qrCode,
            globalQrSettings: fetchedQrConfig.globalSettings,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching branding settings:", error);
      showErrorToast("Failed to load branding settings");
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
  // Convert Map to plain object for JSON serialization
      let searchBoxToSave = { ...settings.searchBox };
      
      // Languages is already an object in frontend (not a Map)
      if (settings.searchBox.languages) {
        // Already an object, use as is
        searchBoxToSave.languages = settings.searchBox.languages;
      } else {
        // Create language structure from current state
        searchBoxToSave.languages = {
          [currentLanguage]: {
            headerText: settings.searchBox.headerText || "Search",
            uploadedUrls: uploadedUrls,
            centerLogo: settings.searchBox.centerLogo || {
              fileUrl: "",
              fileName: "",
              enabled: false,
              width: "100",
              positionTop: "10",
            },
          }
        };
      }
      
      // Update current language content with latest values
      if (searchBoxToSave.languages[currentLanguage]) {
        searchBoxToSave.languages[currentLanguage] = {
          ...searchBoxToSave.languages[currentLanguage],
          headerText: settings.searchBox.headerText || "Search",
          uploadedUrls: uploadedUrls,
          centerLogo: settings.searchBox.centerLogo,
        };
      }

      // Handle placeholder as language-specific object
      const originalPlaceholder = searchBoxToSave._originalPlaceholder || {};
      if (typeof searchBoxToSave.placeholder === 'string') {
        // Current value is a string (the edited value for current language)
        searchBoxToSave.placeholder = {
          ...originalPlaceholder,
          [currentLanguage]: searchBoxToSave.placeholder
        };
      } else if (typeof searchBoxToSave.placeholder === 'object' && searchBoxToSave.placeholder !== null) {
        // Already an object (shouldn't happen with our new logic, but handle it)
        searchBoxToSave.placeholder = {
          ...originalPlaceholder,
          ...searchBoxToSave.placeholder
        };
      } else {
        // No placeholder, create new object with current language
        searchBoxToSave.placeholder = {
          ...originalPlaceholder,
          [currentLanguage]: 'Search...'
        };
      }
      
      // Clean up temporary property
      delete searchBoxToSave._originalPlaceholder;

      // Handle QR Code text as language-specific objects
      let qrCodeToSave = { ...settings.qrCode };
      
      // We need to preserve all languages and only update the current language
      
      // For headerText
      const originalHeaderText = qrCodeToSave._originalHeaderText || {};
      if (typeof qrCodeToSave.headerText === 'string') {
        // Current value is a string (the edited value for current language)
        qrCodeToSave.headerText = {
          ...originalHeaderText,
          [currentLanguage]: qrCodeToSave.headerText
        };
      } else if (typeof qrCodeToSave.headerText === 'object' && qrCodeToSave.headerText !== null) {
        // Already an object (shouldn't happen with our new logic, but handle it)
        qrCodeToSave.headerText = {
          ...originalHeaderText,
          ...qrCodeToSave.headerText
        };
      } else {
        // No headerText, create new object with current language
        qrCodeToSave.headerText = {
          ...originalHeaderText,
          [currentLanguage]: 'Scan Me'
        };
      }
      
      // For footerText
      const originalFooterText = qrCodeToSave._originalFooterText || {};
      if (typeof qrCodeToSave.footerText === 'string') {
        // Current value is a string (the edited value for current language)
        qrCodeToSave.footerText = {
          ...originalFooterText,
          [currentLanguage]: qrCodeToSave.footerText
        };
      } else if (typeof qrCodeToSave.footerText === 'object' && qrCodeToSave.footerText !== null) {
        // Already an object (shouldn't happen with our new logic, but handle it)
        qrCodeToSave.footerText = {
          ...originalFooterText,
          ...qrCodeToSave.footerText
        };
      } else {
        // No footerText, create new object with current language
        qrCodeToSave.footerText = {
          ...originalFooterText,
          [currentLanguage]: 'Get directions on your phone'
        };
      }
      
      // Clean up temporary properties before saving
      delete qrCodeToSave._originalHeaderText;
      delete qrCodeToSave._originalFooterText;

      // Handle Timeout Popup content as language-specific object
      let timeoutPopupToSave = { ...settings.timeoutPopup };
      
      // For timeout popup content
      const originalTimeoutContent = timeoutPopupToSave.text._originalContent || {};
      if (typeof timeoutPopupToSave.text.content === 'string') {
        // Current value is a string (the edited value for current language)
        timeoutPopupToSave.text.content = {
          ...originalTimeoutContent,
          [currentLanguage]: timeoutPopupToSave.text.content
        };
      } else if (typeof timeoutPopupToSave.text.content === 'object' && timeoutPopupToSave.text.content !== null) {
        // Already an object (shouldn't happen with our new logic, but handle it)
        timeoutPopupToSave.text.content = {
          ...originalTimeoutContent,
          ...timeoutPopupToSave.text.content
        };
      } else {
        // No content, create new object with current language
        timeoutPopupToSave.text.content = {
          ...originalTimeoutContent,
          [currentLanguage]: 'Are you still there?'
        };
      }
      
      // Clean up temporary property
      delete timeoutPopupToSave.text._originalContent;

      // Handle Avatar Screen multilanguage fields
      if (timeoutPopupToSave.avatarScreen) {
        // Handle headerText
        const originalAvatarHeaderText = timeoutPopupToSave.avatarScreen._originalHeaderText || {};
        if (typeof timeoutPopupToSave.avatarScreen.headerText === 'string') {
          timeoutPopupToSave.avatarScreen.headerText = {
            ...originalAvatarHeaderText,
            [currentLanguage]: timeoutPopupToSave.avatarScreen.headerText
          };
        } else if (typeof timeoutPopupToSave.avatarScreen.headerText === 'object' && timeoutPopupToSave.avatarScreen.headerText !== null) {
          timeoutPopupToSave.avatarScreen.headerText = {
            ...originalAvatarHeaderText,
            ...timeoutPopupToSave.avatarScreen.headerText
          };
        } else {
          timeoutPopupToSave.avatarScreen.headerText = {
            ...originalAvatarHeaderText,
            [currentLanguage]: 'What would you like to do next?'
          };
        }
        delete timeoutPopupToSave.avatarScreen._originalHeaderText;

        // Handle replayButton.text
        if (timeoutPopupToSave.avatarScreen.replayButton) {
          const originalReplayButtonText = timeoutPopupToSave.avatarScreen.replayButton._originalText || {};
          if (typeof timeoutPopupToSave.avatarScreen.replayButton.text === 'string') {
            timeoutPopupToSave.avatarScreen.replayButton.text = {
              ...originalReplayButtonText,
              [currentLanguage]: timeoutPopupToSave.avatarScreen.replayButton.text
            };
          } else if (typeof timeoutPopupToSave.avatarScreen.replayButton.text === 'object' && timeoutPopupToSave.avatarScreen.replayButton.text !== null) {
            timeoutPopupToSave.avatarScreen.replayButton.text = {
              ...originalReplayButtonText,
              ...timeoutPopupToSave.avatarScreen.replayButton.text
            };
          } else {
            timeoutPopupToSave.avatarScreen.replayButton.text = {
              ...originalReplayButtonText,
              [currentLanguage]: 'Replay Directions'
            };
          }
          delete timeoutPopupToSave.avatarScreen.replayButton._originalText;
        }

        // Handle backToSearchButton.text
        if (timeoutPopupToSave.avatarScreen.backToSearchButton) {
          const originalBackToSearchButtonText = timeoutPopupToSave.avatarScreen.backToSearchButton._originalText || {};
          if (typeof timeoutPopupToSave.avatarScreen.backToSearchButton.text === 'string') {
            timeoutPopupToSave.avatarScreen.backToSearchButton.text = {
              ...originalBackToSearchButtonText,
              [currentLanguage]: timeoutPopupToSave.avatarScreen.backToSearchButton.text
            };
          } else if (typeof timeoutPopupToSave.avatarScreen.backToSearchButton.text === 'object' && timeoutPopupToSave.avatarScreen.backToSearchButton.text !== null) {
            timeoutPopupToSave.avatarScreen.backToSearchButton.text = {
              ...originalBackToSearchButtonText,
              ...timeoutPopupToSave.avatarScreen.backToSearchButton.text
            };
          } else {
            timeoutPopupToSave.avatarScreen.backToSearchButton.text = {
              ...originalBackToSearchButtonText,
              [currentLanguage]: 'Back to Search'
            };
          }
          delete timeoutPopupToSave.avatarScreen.backToSearchButton._originalText;
        }

        // Handle closeWindowButton.text
        if (timeoutPopupToSave.avatarScreen.closeWindowButton) {
          const originalCloseWindowButtonText = timeoutPopupToSave.avatarScreen.closeWindowButton._originalText || {};
          if (typeof timeoutPopupToSave.avatarScreen.closeWindowButton.text === 'string') {
            timeoutPopupToSave.avatarScreen.closeWindowButton.text = {
              ...originalCloseWindowButtonText,
              [currentLanguage]: timeoutPopupToSave.avatarScreen.closeWindowButton.text
            };
          } else if (typeof timeoutPopupToSave.avatarScreen.closeWindowButton.text === 'object' && timeoutPopupToSave.avatarScreen.closeWindowButton.text !== null) {
            timeoutPopupToSave.avatarScreen.closeWindowButton.text = {
              ...originalCloseWindowButtonText,
              ...timeoutPopupToSave.avatarScreen.closeWindowButton.text
            };
          } else {
            timeoutPopupToSave.avatarScreen.closeWindowButton.text = {
              ...originalCloseWindowButtonText,
              [currentLanguage]: 'Close this Window'
            };
          }
          delete timeoutPopupToSave.avatarScreen.closeWindowButton._originalText;
        }
      }

      // Include uploadedUrls in the searchBox settings and ensure logoBeforeMap is included
      const settingsToSave = {
        ...settings,
       searchBox: searchBoxToSave,
        qrCode: qrCodeToSave,
        timeoutPopup: timeoutPopupToSave,
        logoBeforeMap: settings.logoBeforeMap, // Explicitly include logoBeforeMap
        qrCodeConfig: qrCodeConfig, // Include QR code configuration
      };

      console.log("Settings being saved:", settingsToSave);
     console.log("Current language:", currentLanguage);
      console.log("SearchBox languages:", settingsToSave.searchBox.languages);
      console.log("SearchBox placeholder:", settingsToSave.searchBox.placeholder);
      console.log("QR Code texts:", settingsToSave.qrCode.headerText, settingsToSave.qrCode.footerText);
      console.log("Timeout Popup content:", settingsToSave.timeoutPopup.text.content);

      await axios.post(
        `/api/wayfinder/save-branding-settings/${userId}`,
        settingsToSave,
      );
      showMsgSuccessToast(`Branding settings saved successfully for ${getLanguageName(currentLanguage)}!`);
    } catch (error) {
      console.error("Error saving branding settings:", error);
      showErrorToast("Failed to save branding settings");
    }
  };

  // Save QR Code Configuration
  const saveQRCodeConfig = async () => {
    try {
      console.log('=== Frontend saveQRCodeConfig DEBUG ===');
      console.log('qrCodeConfig state:', JSON.stringify(qrCodeConfig, null, 2));
      console.log('enableDirectMapLinkQr value:', qrCodeConfig.enableDirectMapLinkQr);
      
      await axios.post(`/api/wayfinder/qr-config/${userId}`, {
        qrCodeConfig: qrCodeConfig,
      });
      showMsgSuccessToast("QR Code configuration saved successfully!");
    } catch (error) {
      console.error("Error saving QR Code configuration:", error);
      showErrorToast("Failed to save QR Code configuration");
    }
  };

  const fetchCategoryData = async () => {
    try {
      const response = await axios.get(
        `/api/wayfinder/branding-settings/${userId}`,
      );
      if (response.data.success && response.data.data.searchBox?.uploadedUrls) {
        setUploadedUrls(response.data.data.searchBox.uploadedUrls);
      }
    } catch (error) {
      console.error("Error fetching category data:", error);
    }
  };

  const handleUpload = async (file, position) => {
    if (!file) {
      showErrorToast("Please select a file first");
      return;
    }

    setUploadingStates((prev) => ({ ...prev, [position]: true }));
    setUploadingLogoPosition(position); // Set the position before upload

    try {
      const fileUrl = await uploadFile(file);
      const fileName = file.name;

      // Update the uploadedUrls state
      setUploadedUrls((prev) => ({
        ...prev,
        [position]: {
          ...prev[position],
          fileUrl: fileUrl,
          fileName: fileName,
          enabled: true,
        },
      }));

      showMsgSuccessToast(`${position} logo uploaded successfully!`);
    } catch (error) {
      console.error("Upload error:", error);
      showErrorToast("Failed to upload file");
    } finally {
      setUploadingStates((prev) => ({ ...prev, [position]: false }));
      setUploadingLogoPosition(null); // Clear the position after upload
    }
  };

  const handleDeleteLogo = (position) => {
    setUploadedUrls((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        fileUrl: "",
        fileName: "",
        enabled: false,
      },
    }));
    showMsgSuccessToast(`${position} logo removed`);
  };

  const handleToggleChangeMedia = (position, value) => {
    setUploadedUrls((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        enabled: value,
      },
    }));
  };

  const handleCenterLogoUpdate = (value) => {
    setSettings((prev) => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        centerLogo: {
          ...prev.searchBox.centerLogo,
          enabled: value,
        },
      },
    }));
  };

  const handleSave = () => {
    saveSettings();
  };

  // ==================== UPDATE FUNCTIONS ====================

  // Search Box Updates
  const updateSearchBox = (field, value) => {
    // Special handling for placeholder to preserve multilingual data
    if (field === 'placeholder') {
      setSettings((prev) => {
        const originalPlaceholder = prev.searchBox._originalPlaceholder || {};
        
        return {
          ...prev,
          searchBox: {
            ...prev.searchBox,
            placeholder: value,
            // Keep the original multilingual object updated with current language
            _originalPlaceholder: {
              ...originalPlaceholder,
              [currentLanguage]: value
            }
          },
        };
      });
    } else {
      setSettings((prev) => ({
        ...prev,
        searchBox: {
          ...prev.searchBox,
          [field]: value,
        },
      }));
    }
  };

  const updateSearchBoxPosition = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        [field]: value,
      },
    }));
  };

  const updateSearchBoxBorder = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        border: {
          ...prev.searchBox.border,
          [field]: value,
        },
      },
    }));
  };

  const updateSearchBoxShadow = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        boxShadow: {
          ...prev.searchBox.boxShadow,
          [field]: value,
        },
      },
    }));
  };

  // Keyboard Updates
  const updateKeyboard = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        keyboard: {
          ...prev.searchBox.keyboard,
          [field]: value,
        },
      },
    }));
  };

  const updateKeyboardShadow = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      searchBox: {
        ...prev.searchBox,
        keyboard: {
          ...prev.searchBox.keyboard,
          shadow: {
            ...prev.searchBox.keyboard.shadow,
            [field]: value,
          },
        },
      },
    }));
  };

  // Back Button Updates
  const updateBackButton = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      backButton: {
        ...prev.backButton,
        [field]: value,
      },
    }));
  };

  const updateBackButtonPosition = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      backButton: {
        ...prev.backButton,
        position: {
          ...prev.backButton.position,
          [field]: value,
        },
      },
    }));
  };

  const updateBackButtonRadius = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      backButton: {
        ...prev.backButton,
        borderRadius: {
          ...prev.backButton.borderRadius,
          [field]: value,
        },
      },
    }));
  };

  const updateBackButtonShadow = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      backButton: {
        ...prev.backButton,
        shadow: {
          ...prev.backButton.shadow,
          [field]: value,
        },
      },
    }));
  };

  // QR Code Updates
  const updateQRCode = (field, value) => {
    // Special handling for headerText and footerText to preserve multilingual data
    if (field === 'headerText' || field === 'footerText') {
      setSettings((prev) => {
        const originalKey = `_original${field.charAt(0).toUpperCase() + field.slice(1)}`;
        const originalData = prev.qrCode[originalKey] || {};
        
        return {
          ...prev,
          qrCode: {
            ...prev.qrCode,
            [field]: value,
            // Keep the original multilingual object updated with current language
            [originalKey]: {
              ...originalData,
              [currentLanguage]: value
            }
          },
        };
      });
    } else {
      setSettings((prev) => ({
        ...prev,
        qrCode: {
          ...prev.qrCode,
          [field]: value === "" || value === null ? null : value,
        },
      }));
    }
  };

  // Map Position Updates
  const updateMapPosition = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      mapPosition: {
        ...prev.mapPosition,
        [field]: value === "" || value === null ? null : value,
      },
    }));
  };

  const updateMapPositionShadow = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      mapPosition: {
        ...prev.mapPosition,
        boxShadow: {
          ...prev.mapPosition.boxShadow,
          [field]: value,
        },
      },
    }));
  };

  // Logo Before Map Updates
  const updateLogoBeforeMap = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      logoBeforeMap: {
        ...prev.logoBeforeMap,
        [field]: value,
      },
    }));
  };

  // Timeout Popup Updates
  const updateTimeoutPopup = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      timeoutPopup: {
        ...prev.timeoutPopup,
        [field]: value,
      },
    }));
  };

  const updateTimeoutText = (field, value) => {
    // Special handling for content to preserve multilingual data
    if (field === 'content') {
      setSettings((prev) => {
        const originalContent = prev.timeoutPopup.text._originalContent || {};
        
        return {
          ...prev,
          timeoutPopup: {
            ...prev.timeoutPopup,
            text: {
              ...prev.timeoutPopup.text,
              content: value,
              // Keep the original multilingual object updated with current language
              _originalContent: {
                ...originalContent,
                [currentLanguage]: value
              }
            },
          },
        };
      });
    } else {
      setSettings((prev) => ({
        ...prev,
        timeoutPopup: {
          ...prev.timeoutPopup,
          text: {
            ...prev.timeoutPopup.text,
            [field]: value,
          },
        },
      }));
    }
  };

  const updateTimeoutButton = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      timeoutPopup: {
        ...prev.timeoutPopup,
        button: {
          ...prev.timeoutPopup.button,
          [field]: value,
        },
      },
    }));
  };

  const updateTimeoutButtonShadow = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      timeoutPopup: {
        ...prev.timeoutPopup,
        button: {
          ...prev.timeoutPopup.button,
          shadow: {
            ...prev.timeoutPopup.button.shadow,
            [field]: value,
          },
        },
      },
    }));
  };

  // Avatar Screen Updates
  const updateAvatarScreen = (field, value) => {
    console.log('updateAvatarScreen called with:', field, value);
    
    // Special handling for headerText to preserve multilingual data
    if (field === 'headerText') {
      setSettings((prev) => {
        const originalHeaderText = prev.timeoutPopup.avatarScreen._originalHeaderText || {};
        
        return {
          ...prev,
          timeoutPopup: {
            ...prev.timeoutPopup,
            avatarScreen: {
              ...prev.timeoutPopup.avatarScreen,
              headerText: value,
              // Keep the original multilingual object updated with current language
              _originalHeaderText: {
                ...originalHeaderText,
                [currentLanguage]: value
              }
            },
          },
        };
      });
    } else {
      setSettings((prev) => ({
        ...prev,
        timeoutPopup: {
          ...prev.timeoutPopup,
          avatarScreen: {
            ...prev.timeoutPopup.avatarScreen,
            [field]: value,
          },
        },
      }));
    }
  };

  const updateAvatarScreenButton = (buttonType, field, value) => {
    console.log('updateAvatarScreenButton called with:', buttonType, field, value);
    
    // Special handling for text field to preserve multilingual data
    if (field === 'text') {
      setSettings((prev) => {
        const originalText = prev.timeoutPopup.avatarScreen[buttonType]._originalText || {};
        
        return {
          ...prev,
          timeoutPopup: {
            ...prev.timeoutPopup,
            avatarScreen: {
              ...prev.timeoutPopup.avatarScreen,
              [buttonType]: {
                ...prev.timeoutPopup.avatarScreen[buttonType],
                text: value,
                // Keep the original multilingual object updated with current language
                _originalText: {
                  ...originalText,
                  [currentLanguage]: value
                }
              },
            },
          },
        };
      });
    } else {
      setSettings((prev) => ({
        ...prev,
        timeoutPopup: {
          ...prev.timeoutPopup,
          avatarScreen: {
            ...prev.timeoutPopup.avatarScreen,
            [buttonType]: {
              ...prev.timeoutPopup.avatarScreen[buttonType],
              [field]: value,
            },
          },
        },
      }));
    }
  };

  const updateAvatarScreenButtonShadow = (buttonType, field, value) => {
    console.log('updateAvatarScreenButtonShadow called with:', buttonType, field, value);
    setSettings((prev) => ({
      ...prev,
      timeoutPopup: {
        ...prev.timeoutPopup,
        avatarScreen: {
          ...prev.timeoutPopup.avatarScreen,
          [buttonType]: {
            ...prev.timeoutPopup.avatarScreen[buttonType],
            shadow: {
              ...prev.timeoutPopup.avatarScreen[buttonType].shadow,
              [field]: value,
            },
          },
        },
      },
    }));
  };

  const updateCustomImage = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      backButton: {
        ...prev.backButton,
        buttonImage: {
          ...prev.backButton.buttonImage,
          [field]: value,
        },
      },
    }));
  };

  const handleImageUpload = async (event) => {
    let file;

    if (event.target?.files) {
      file = event.target.files[0];
    } else if (Array.isArray(event)) {
      file = event[0];
    } else if (selectedBackButtonFile) {
      file = selectedBackButtonFile;
    }

    if (!file) {
      showErrorToast("Please select a file first");
      return;
    }

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file (PNG, JPG, etc.)");
      return;
    }

    try {
      await uploadFile(file);
      // Clear the selected file after successful upload
      setSelectedBackButtonFile(null);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleRemoveImage = () => {
    updateBackButton("buttonImage", {
      ...settings.backButton.buttonImage,
      fileUrl: "",
      fileName: "",
    });
    setSelectedBackButtonFile(null);
    showMsgSuccessToast("Image removed");
  };

  const handleCenterLogoUpload = async (file) => {
    if (!file) {
      showErrorToast("Please select a file first");
      return;
    }

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file (PNG, JPG, etc.)");
      return;
    }

    try {
     const fileUrl = await uploadFile(file);
      
      // Update center logo for current language
      const updatedCenterLogo = {
        ...settings.searchBox.centerLogo,
        fileUrl: fileUrl,
        fileName: file.name,
        enabled: true,
      };
      
      updateSearchBox("centerLogo", updatedCenterLogo);
      
      // Also update in language-specific structure (using object notation, not Map)
      setSettings((prev) => {
        const updatedSettings = { ...prev };
        
        if (!updatedSettings.searchBox.languages) {
          updatedSettings.searchBox.languages = {};
        }
        
        const currentLangContent = updatedSettings.searchBox.languages[currentLanguage] || {
          headerText: updatedSettings.searchBox.headerText || "Search",
          uploadedUrls: uploadedUrls,
          centerLogo: updatedCenterLogo,
        };
        
        currentLangContent.centerLogo = updatedCenterLogo;
        updatedSettings.searchBox.languages[currentLanguage] = currentLangContent;
        
        return updatedSettings;
      });
      
      setSelectedCenterLogoFile(null);

      showMsgSuccessToast(`Center logo uploaded for ${getLanguageName(currentLanguage)}`);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleRemoveCenterLogo = () => {
    updateSearchBox("centerLogo", {
      ...settings.searchBox.centerLogo,
      fileUrl: "",
      fileName: "",
      enabled: false,
    });
    setSelectedCenterLogoFile(null);
    showMsgSuccessToast("Center logo removed");
  };

  // Logo Before Map Functions
  const handleLogoBeforeMapUpload = async (file) => {
    if (!file) {
      showErrorToast("Please select a file first");
      return;
    }

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file (PNG, JPG, etc.)");
      return;
    }

    try {
      await uploadFile(file);
      setSelectedLogoBeforeMapFile(null);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleRemoveLogoBeforeMap = () => {
    updateLogoBeforeMap("fileUrl", "");
    updateLogoBeforeMap("fileName", "");
    updateLogoBeforeMap("enabled", false);
    setSelectedLogoBeforeMapFile(null);
    showMsgSuccessToast("Logo before map removed");
  };

  // ==================== QR CODE GENERATOR FUNCTIONS ====================

  const handleQrSettingChange = (section, key, value) => {
    setQrSettings(prevSettings => ({
      ...prevSettings,
      [section]: {
        ...prevSettings[section],
        [key]: value,
      },
    }));

    // Also update the qrCodeConfig
    setQrCodeConfig(prevConfig => ({
      ...prevConfig,
      globalSettings: {
        ...prevConfig.globalSettings,
        [section]: {
          ...prevConfig.globalSettings[section],
          [key]: value,
        },
      },
    }));

    // Also update the main settings state to ensure it's saved
    setSettings(prevSettings => ({
      ...prevSettings,
      qrCode: {
        ...prevSettings.qrCode,
        globalQrSettings: {
          ...prevSettings.qrCode.globalQrSettings,
          [section]: {
            ...prevSettings.qrCode.globalQrSettings[section],
            [key]: value,
          },
        },
      },
    }));
  };

  const handleQrConfigChange = (key, value) => {
    console.log(`=== handleQrConfigChange DEBUG ===`);
    console.log(`Changing ${key} to:`, value);
    
    setQrCodeConfig(prevConfig => {
      const newConfig = {
        ...prevConfig,
        [key]: value,
      };
      console.log('New qrCodeConfig:', newConfig);
      return newConfig;
    });

    // Also update the main settings state
    setSettings(prevSettings => ({
      ...prevSettings,
      qrCodeConfig: {
        ...prevSettings.qrCodeConfig,
        [key]: value,
      },
    }));
  };

  const handleQrLogoUpload = async (file) => {
    if (!file) {
      showErrorToast('Please select a file first!');
      return;
    }

    try {
      const fileUrl = await uploadFile(file);
      setQrSettings(prevSettings => ({
        ...prevSettings,
        logoImage: fileUrl,
      }));
      
      // Also update qrCodeConfig
      setQrCodeConfig(prevConfig => ({
        ...prevConfig,
        globalSettings: {
          ...prevConfig.globalSettings,
          logoImage: fileUrl,
        },
      }));

      // Also update the main settings state
      setSettings(prevSettings => ({
        ...prevSettings,
        qrCode: {
          ...prevSettings.qrCode,
          globalQrSettings: {
            ...prevSettings.qrCode.globalQrSettings,
            logoImage: fileUrl,
          },
        },
      }));
      
      setQrLogo(null);
      showMsgSuccessToast('QR Logo uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      showErrorToast('Failed to upload QR logo');
    }
  };

  const generateGlobalQRCode = async () => {
    try {
      setQrGenerating(true);
      setQrGenerationProgress(0);
      setQrGenerationMessage('Initializing...');

      // First save the current QR configuration
      await saveQRCodeConfig();

      // Step 1: Generate global wayfinder QR code
      setQrGenerationMessage('Generating global QR code...');
      showMsgSuccessToast('Generating global QR code...');
      const globalResponse = await axios.post(
        `/api/wayfinder/branding-settings/${userId}/generate-global-qr`,
        {
          qrSettings: qrCodeConfig.globalSettings,
          qrText: qrText,
        },
        {
          timeout: 300000, // 5 minutes timeout
        }
      );

      if (!globalResponse.data.success) {
        throw new Error('Failed to generate global QR code');
      }

      // Update settings with the new global QR code
      setSettings(prev => ({
        ...prev,
        qrCode: {
          ...prev.qrCode,
          generatedQrUrl: globalResponse.data.data.globalQrCode.qrCodeUrl,
          globalQrSettings: qrCodeConfig.globalSettings,
        }
      }));

      const totalLocations = globalResponse.data.data.locationQrCodes.total;
      
      if (totalLocations === 0) {
        setQrGenerationProgress(100);
        setQrGenerationMessage('Complete!');
        showMsgSuccessToast('Global QR code generated successfully! No locations found.');
        return;
      }

      showMsgSuccessToast(`Global QR code generated! Starting QR generation for ${totalLocations} locations...`);

      // Step 2: Initialize location QR generation session
      setQrGenerationMessage(`Initializing QR generation for ${totalLocations} locations...`);
      const initResponse = await axios.post(
        `/api/wayfinder/branding-settings/${userId}/initialize-location-qr`,
        { 
          qrSettings: qrCodeConfig.globalSettings,
          language: currentLanguage // Pass current language
        },
        {
          timeout: 300000, // 5 minutes timeout
        }
      );

      const { sessionId, totalChunks } = initResponse.data;
      console.log(`QR Session initialized: ${totalChunks} chunks for ${totalLocations} locations`);

      // Step 3: Process each chunk
      let successfulTotal = 0;
      let failedTotal = 0;

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const chunkResponse = await axios.post(
          `/api/wayfinder/branding-settings/${userId}/process-location-qr-chunk`,
          { sessionId, chunkIndex },
          {
            timeout: 300000, // 5 minutes timeout per chunk
          }
        );

        const { chunkResults, progress } = chunkResponse.data;
        successfulTotal += chunkResults.successful;
        failedTotal += chunkResults.failed;

        // Update progress state
        const percentage = Math.round((progress.processedChunks / totalChunks) * 100);
        setQrGenerationProgress(percentage);
        setQrGenerationMessage(
          `Processing: ${progress.processedLocations}/${totalLocations} locations (${successfulTotal} successful${failedTotal > 0 ? `, ${failedTotal} failed` : ''})`
        );

        showMsgSuccessToast(
          `Processing QR codes: ${percentage}% (${progress.processedLocations}/${totalLocations} locations)`
        );

        console.log(`Chunk ${chunkIndex}: ${chunkResults.successful} successful, ${chunkResults.failed} failed`);

        // Small delay between chunks
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Step 4: Finalize and get complete results
      setQrGenerationMessage('Finalizing...');
      const finalResponse = await axios.get(
        `/api/wayfinder/branding-settings/${userId}/finalize-location-qr/${sessionId}`,
        {
          timeout: 60000, // 1 minute timeout
        }
      );

      const finalData = finalResponse.data.data;
      
      // Show final summary
      setQrGenerationProgress(100);
      setQrGenerationMessage(`Complete! ${finalData.successful} QR codes generated${finalData.failed > 0 ? `, ${finalData.failed} failed` : ''}`);
      
      const summaryText = `QR generation complete! Global QR + ${finalData.successful} location QR codes generated for ${getLanguageName(currentLanguage)}${finalData.failed > 0 ? `, ${finalData.failed} failed` : ''}.`;
      showMsgSuccessToast(summaryText);

      console.log('QR Generation Complete:', {
        globalQrCode: globalResponse.data.data.globalQrCode,
        locationResults: finalData,
        language: currentLanguage
      });

    } catch (err) {
      console.error('QR generation error:', err);
      setQrGenerationMessage('Error occurred during generation');
      showErrorToast(err.response?.data?.message || err.message || 'Failed to generate QR codes');
    } finally {
      setQrGenerating(false);
    }
  };

  // Function to regenerate QR codes for all locations only (using chunked approach)
  const regenerateLocationQRCodes = async () => {
    try {
      setQrGenerating(true);

      // Step 1: Initialize location QR generation session
      const initResponse = await axios.post(
        `/api/wayfinder/branding-settings/${userId}/initialize-location-qr`,
        { qrSettings: qrCodeConfig.globalSettings }
      );

      const { sessionId, totalChunks, totalLocations } = initResponse.data;
      
      if (totalLocations === 0) {
        showMsgSuccessToast('No locations found to regenerate QR codes.');
        return;
      }

      showMsgSuccessToast(`Starting QR regeneration for ${totalLocations} locations...`);
      console.log(`QR Session initialized: ${totalChunks} chunks for ${totalLocations} locations`);

      // Step 2: Process each chunk
      let successfulTotal = 0;
      let failedTotal = 0;

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const chunkResponse = await axios.post(
          `/api/wayfinder/branding-settings/${userId}/process-location-qr-chunk`,
          { sessionId, chunkIndex }
        );

        const { chunkResults, progress } = chunkResponse.data;
        successfulTotal += chunkResults.successful;
        failedTotal += chunkResults.failed;

        // Update progress message
        const percentage = Math.round((progress.processedChunks / totalChunks) * 100);
        showMsgSuccessToast(
          `Regenerating QR codes: ${percentage}% (${progress.processedLocations}/${totalLocations} locations)`
        );

        console.log(`Chunk ${chunkIndex}: ${chunkResults.successful} successful, ${chunkResults.failed} failed`);

        // Small delay between chunks
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Step 3: Finalize and get complete results
      const finalResponse = await axios.get(
        `/api/wayfinder/branding-settings/${userId}/finalize-location-qr/${sessionId}`
      );

      const finalData = finalResponse.data.data;
      
      // Show final summary
      const summaryText = `Location QR codes regenerated! ${finalData.successful} successful${finalData.failed > 0 ? `, ${finalData.failed} failed` : ''}.`;
      showMsgSuccessToast(summaryText);

      console.log('Location QR Regeneration Complete:', finalData);

    } catch (err) {
      console.error('Location QR regeneration error:', err);
      showErrorToast(err.response?.data?.message || err.message || 'Failed to regenerate location QR codes');
    } finally {
      setQrGenerating(false);
    }
  };

  /**
   * Generate direct map link QR codes for all locations in bulk (chunked approach)
   */
  const handleGenerateBulkDirectMapQR = async () => {
    try {
      setGeneratingDirectMapQR(true);
      setDirectMapQRProgress(0);
      setDirectMapQRStatus('Initializing direct map link QR generation...');

      // Step 1: Initialize the chunked generation with current language
      const initResponse = await axios.post(
        `/api/locations/${userId}/direct-map-qr/initialize`,
        {
          language: currentLanguage // Pass current language
        }
      );

      if (!initResponse.data.success) {
        throw new Error(initResponse.data.message);
      }

      const { sessionId, totalChunks, totalLocations } = initResponse.data;
      console.log(`📊 Initialized session: ${sessionId}, ${totalLocations} locations in ${totalChunks} chunks for language: ${currentLanguage}`);

      setDirectMapQRStatus(`Processing ${totalLocations} locations in ${totalChunks} chunks for ${getLanguageName(currentLanguage)}...`);

      // Step 2: Process each chunk sequentially
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        setDirectMapQRStatus(`Processing chunk ${chunkIndex + 1} of ${totalChunks}...`);

        const chunkResponse = await axios.post(
          `/api/locations/${userId}/direct-map-qr/process-chunk`,
          {
            sessionId,
            chunkIndex,
          }
        );

        if (chunkResponse.data.success) {
          const progress = chunkResponse.data.progress;
          const percentage = Math.round((progress.processedChunks / progress.totalChunks) * 100);
          setDirectMapQRProgress(percentage);

          console.log(`✅ Chunk ${chunkIndex + 1}/${totalChunks} complete: ${chunkResponse.data.chunkResults.successful} successful, ${chunkResponse.data.chunkResults.failed} failed`);
        } else {
          console.error(`❌ Chunk ${chunkIndex + 1} failed:`, chunkResponse.data.message);
        }
      }

      // Step 3: Finalize and get results
      setDirectMapQRStatus('Finalizing...');
      const finalResponse = await axios.get(
        `/api/locations/${userId}/direct-map-qr/finalize/${sessionId}`
      );

      if (finalResponse.data.success) {
        setDirectMapQRProgress(100);
        setDirectMapQRStatus('Complete!');
        showMsgSuccessToast(`${finalResponse.data.message} for ${getLanguageName(currentLanguage)}`);
        console.log(`🎉 Direct map link QR generation complete for ${currentLanguage}:`, finalResponse.data.data);
      }
    } catch (error) {
      console.error('❌ Error generating bulk direct map link QR codes:', error);
      showErrorToast(error.response?.data?.message || error.message || 'Failed to generate bulk direct map link QR codes');
      setDirectMapQRStatus('Failed');
    } finally {
      setTimeout(() => {
        setGeneratingDirectMapQR(false);
        setDirectMapQRProgress(0);
        setDirectMapQRStatus('');
      }, 3000); // Keep the success/error message visible for 3 seconds
    }
  };

  const renderQrPatternSettings = () => {
    const patterns = [
      {
        id: 1,
        type: 'frame',
        options: [
          { value: 'banner-bottom', src: 'https://dashboard.uniqode.com/assets/img/frames/banner-bottom.svg' },
          { value: 'balloon-bottom', src: 'https://dashboard.uniqode.com/assets/img/frames/balloon-bottom.svg' },
          { value: 'box-bottom', src: 'https://dashboard.uniqode.com/assets/img/frames/box-bottom.svg' },
          { value: 'banner-top', src: 'https://dashboard.uniqode.com/assets/img/frames/banner-top.svg' },
          { value: 'box-top', src: 'https://dashboard.uniqode.com/assets/img/frames/box-top.svg' },
          { value: 'balloon-top', src: 'https://dashboard.uniqode.com/assets/img/frames/balloon-top.svg' },
          { value: 'text-only', src: 'https://dashboard.uniqode.com/assets/img/frames/text-only.svg' },
          { value: 'none', src: 'none' },
        ],
      },
      {
        id: 2,
        type: 'logo',
        options: null,
      },
      {
        id: 3,
        type: 'eyes',
        options: [
          { value: 'square', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/square.svg' },
          { value: 'rounded', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/rounded.svg' },
          { value: 'circle', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/circle.svg' },
          { value: 'left-diamond', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/left-diamond.svg' },
          { value: 'right-diamond', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/right-diamond.svg' },
          { value: 'left-leaf', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/left-leaf.svg' },
          { value: 'right-leaf', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-balls/right-leaf.svg' },
        ],
        eyeFrame: [
          { value: 'square', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-frames/square.svg' },
          { value: 'rounded', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-frames/rounded.svg' },
          { value: 'circle', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-frames/circle.svg' },
          { value: 'left-leaf', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-frames/left-leaf.svg' },
          { value: 'right-leaf', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/eye-frames/right-leaf.svg' },
        ],
      },
      {
        id: 4,
        type: 'pattern',
        options: [
          { value: 'square', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/square.svg' },
          { value: 'circle', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/circle.svg' },
          { value: 'kite', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/kite.svg' },
          { value: 'left-diamond', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/left-diamond.svg' },
          { value: 'right-diamond', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/right-diamond.svg' },
          { value: 'thin-square', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/thin-square.svg' },
          { value: 'smooth-round', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/smooth-round.svg' },
          { value: 'smooth-sharp', src: 'https://static.uniqode.com/assets/img/mobstac-awesome-qr/data-patterns/smooth-sharp.svg' },
        ],
      },
    ];

    const selectedPatternData = patterns.find(p => p.id === selectedQrPattern);
    if (!selectedPatternData) return null;

    switch (selectedPatternData.type) {
      case 'frame':
        return (
          <div className="menu-card2">
            <div style={{ display: 'flex', gap: '55px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px', padding: '11px' }}>
              {selectedPatternData.options.map((pattern, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      border: `3px solid ${qrCodeConfig.globalSettings?.frame?.frameStyle === pattern.value ? '#4caf50' : 'transparent'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'border-color 0.3s',
                      padding: '5px',
                    }}
                  >
                    {pattern.value === 'none' ? (
                      <p
                        style={{ width: '100px', height: '100px', borderRadius: '5px', padding: '28px' }}
                        onClick={() => handleQrSettingChange('frame', 'frameStyle', pattern.value)}
                      >
                        {pattern.value}
                      </p>
                    ) : (
                      <img
                        src={pattern.src}
                        alt={pattern.value}
                        style={{ width: '100px', objectFit: 'cover', borderRadius: '5px' }}
                        onClick={() => handleQrSettingChange('frame', 'frameStyle', pattern.value)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="row" style={{ marginTop: '20px' }}>
              <div className="form-group col-md-4">
                <label>Select Frame Color:</label>
                <MuiColorInput
                  className="form-control"
                  format="hex"
                  value={qrSettings.frame.frameColor}
                  onChange={newHex => handleQrSettingChange('frame', 'frameColor', newHex?.toUpperCase?.())}
                  isAlphaHidden={true}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0px !important', height: 53 },
                    '& .MuiOutlinedInput-input': { height: '36px' },
                    '& .MuiColorInput-Button': { width: '49px !important', height: '40px !important' },
                    '& .MuiInputAdornment-root .MuiBox-root': { width: 18, height: 18 },
                  }}
                />
              </div>
              
              <div className="form-group col-md-4">
                <label>Enter Frame Text:</label>
                <input
                  type="text"
                  placeholder="Enter text"
                  className="form-control"
                  value={qrSettings.frame.frameText}
                  onChange={e => handleQrSettingChange('frame', 'frameText', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 'eyes':
        return (
          <div className="menu-card2">
            <div style={{ display: 'flex', gap: '55px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px', padding: '11px' }}>
              {selectedPatternData.options.map((pattern, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      border: `3px solid ${qrSettings?.eyes.eyeFrameShape === pattern.value ? '#4caf50' : 'transparent'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'border-color 0.3s',
                      padding: '5px',
                    }}
                  >
                    <img
                      src={pattern.src}
                      alt=""
                      style={{ width: '100px', objectFit: 'cover', borderRadius: '5px' }}
                      onClick={() => handleQrSettingChange('eyes', 'eyeFrameShape', pattern.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="row" style={{ marginTop: '20px' }}>
              <div className="form-group col-md-6">
                <label>Select Eye Ball Color:</label>
                <MuiColorInput
                  className="form-control"
                  format="hex"
                  value={qrSettings.eyes.eyeBallColor}
                  onChange={newHex => handleQrSettingChange('eyes', 'eyeBallColor', newHex?.toUpperCase?.())}
                  isAlphaHidden={true}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0px !important', height: 53 },
                    '& .MuiOutlinedInput-input': { height: '36px' },
                    '& .MuiColorInput-Button': { width: '49px !important', height: '40px !important' },
                    '& .MuiInputAdornment-root .MuiBox-root': { width: 18, height: 18 },
                  }}
                />
              </div>
              
              <div className="form-group col-md-6">
                <label>Select Eye Frame Color:</label>
                <MuiColorInput
                  className="form-control"
                  format="hex"
                  value={qrSettings.eyes.eyeFrameColor}
                  onChange={newHex => handleQrSettingChange('eyes', 'eyeFrameColor', newHex?.toUpperCase?.())}
                  isAlphaHidden={true}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0px !important', height: 53 },
                    '& .MuiOutlinedInput-input': { height: '36px' },
                    '& .MuiColorInput-Button': { width: '49px !important', height: '40px !important' },
                    '& .MuiInputAdornment-root .MuiBox-root': { width: 18, height: 18 },
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '55px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px', padding: '11px' }}>
              {selectedPatternData.eyeFrame.map((pattern, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      border: `3px solid ${qrSettings?.eyes.eyeBallShape === pattern.value ? '#4caf50' : 'transparent'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'border-color 0.3s',
                      padding: '5px',
                    }}
                  >
                    <img
                      src={pattern.src}
                      alt=""
                      style={{ width: '50px', objectFit: 'cover', borderRadius: '5px' }}
                      onClick={() => handleQrSettingChange('eyes', 'eyeBallShape', pattern.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'pattern':
        return (
          <div className="menu-card2">
            <div style={{ display: 'flex', gap: '55px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px', padding: '11px' }}>
              {selectedPatternData.options.map((pattern, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      border: `3px solid ${qrSettings?.patterns.dataPattern === pattern.value ? '#4caf50' : 'transparent'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'border-color 0.3s',
                      padding: '5px',
                    }}
                  >
                    <img
                      src={pattern.src}
                      alt=""
                      style={{ width: '100px', objectFit: 'cover', borderRadius: '5px' }}
                      onClick={() => handleQrSettingChange('patterns', 'dataPattern', pattern.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="row" style={{ marginTop: '20px' }}>
              <div className="form-group col-md-12">
                <label>Select Pattern Color:</label>
                <MuiColorInput
                  className="form-control"
                  format="hex"
                  value={qrSettings.patterns.colorDark}
                  onChange={newHex => handleQrSettingChange('patterns', 'colorDark', newHex?.toUpperCase?.())}
                  isAlphaHidden={true}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '0px !important', height: 53 },
                    '& .MuiOutlinedInput-input': { height: '36px' },
                    '& .MuiColorInput-Button': { width: '49px !important', height: '40px !important' },
                    '& .MuiInputAdornment-root .MuiBox-root': { width: 18, height: 18 },
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'logo':
        return (
          <div className="row menu-card2">
            <div className="form-group col-md-6">
              <label className="d-flex align-items-center" style={{ height: '25px' }}>
                Select Background Color: (Transparent
                <input
                  type="checkbox"
                  className="ml-1 mr-1"
                  checked={qrSettings.background.backgroundColor === 'transparent'}
                  onChange={e =>
                    handleQrSettingChange(
                      'background',
                      'backgroundColor',
                      e.target.checked ? 'transparent' : '#000000'
                    )
                  }
                />
                )
              </label>
              <MuiColorInput
                className="form-control"
                format="hex"
                value={qrSettings.background.backgroundColor}
                onChange={newHex =>
                  handleQrSettingChange('background', 'backgroundColor', newHex?.toUpperCase?.())
                }
                disabled={qrSettings.background.backgroundColor === 'transparent'}
                isAlphaHidden={true}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '0px !important', height: 53 },
                  '& .MuiOutlinedInput-input': { height: '36px' },
                  '& .MuiColorInput-Button': { width: '49px !important', height: '40px !important' },
                  '& .MuiInputAdornment-root .MuiBox-root': { width: 18, height: 18 },
                }}
              />
            </div>
            
            <div className="col-md-6">
              <div className="form-group" style={{ height: '330px' }}>
                <label>Upload QR Logo:</label>
                <div
                  style={{
                    border: '2px dashed #cccccc',
                    padding: '20px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => document.getElementById('qr-logo-input').click()}
                >
                  <input
                    id="qr-logo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setQrLogo(e.target.files[0]);
                      }
                    }}
                  />
                  {qrLogo ? (
                    <p>{qrLogo.name}</p>
                  ) : (
                    <p>Drag and drop a file here, or click to select a file</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleQrLogoUpload(qrLogo)}
                  style={{
                    marginTop: '5px',
                    backgroundColor: buttonColor,
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    padding: '10px 20px',
                  }}
                  disabled={!qrLogo}
                >
                  Upload
                </button>
                {qrSettings?.logoImage && (
                  <div style={{ marginTop: '20px' }}>
                    <img style={{ width: '150px', padding: '10px' }} src={qrSettings.logoImage} alt="QR Logo" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ==================== LOGO HANDLING FUNCTIONS ====================

  const handleLogoUploadSuccess = (position, fileUrl, fileName) => {
    setUploadedUrls((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        fileUrl: fileUrl,
        fileName: fileName,
        enabled: true,
      },
    }));

     // Also update settings to ensure language-specific structure is used
    setSettings((prev) => {
      const updatedSettings = { ...prev };
      
      // Initialize languages object if it doesn't exist (it's an object, not a Map in frontend)
      if (!updatedSettings.searchBox.languages) {
        updatedSettings.searchBox.languages = {};
      }
      
      // Get current language content or create default
      const currentLangContent = updatedSettings.searchBox.languages[currentLanguage] || {
        headerText: updatedSettings.searchBox.headerText || "Search",
        uploadedUrls: {
          upperLeft: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
          upperRight: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
          bottomLeft: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
          bottomRight: { fileUrl: "", fileName: "", enabled: false, width: 100, height: 100 },
        },
        centerLogo: {
          fileUrl: "",
          fileName: "",
          enabled: false,
          width: "100",
          positionTop: "10",
        },
      };
      
      // Update the specific position
      currentLangContent.uploadedUrls[position] = {
        ...currentLangContent.uploadedUrls[position],
        fileUrl: fileUrl,
        fileName: fileName,
        enabled: true,
      };
      
      // Set the updated language content (using object notation, not Map)
      updatedSettings.searchBox.languages[currentLanguage] = currentLangContent;
      updatedSettings.searchBox.defaultLanguage = currentLanguage;
      
      return updatedSettings;
    });

    setUploadingLogoPosition(null);
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (userId) {
  // Try to get saved language preference from localStorage
      const savedLang = localStorage.getItem(`language_pref_${userId}`);
      if (savedLang) {
        setCurrentLanguage(savedLang);
      }
      
      // Fetch available languages
      fetchAvailableLanguages();
      
      // Fetch settings with saved or default language
      fetchSettings();
      
      // Fetch step-by-step directions configuration
      fetchStepByStepConfig();
    }
  }, [userId]);

  useEffect(() => {
    if (userId && currentLanguage) {
      fetchSettings();
    }
  }, [currentLanguage]);

  // Add effect to listen for language changes from header dropdown
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === `language_pref_${userId}` && e.newValue) {
        const newLang = e.newValue;
        if (newLang !== currentLanguage) {
          // Language changed from another tab or component
          handleLanguageChange(newLang);
        }
      }
    };

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also check localStorage periodically for changes from same tab
    const intervalId = setInterval(() => {
      const savedLang = localStorage.getItem(`language_pref_${userId}`);
      if (savedLang && savedLang !== currentLanguage) {
        handleLanguageChange(savedLang);
      }
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(intervalId);
    };
  }, [userId, currentLanguage]);

  // ==================== RENDER FUNCTIONS ====================

  function getPreviewTitle() {
    switch (previewType) {
      case "search":
        return "Search Page";
      case "timeout":
        return "Timeout Popup";
      case "back":
        return "Back Button";
      default:
        return "";
    }
  }

  function renderPreviewContent() {
    switch (previewType) {
      case "search":
        return renderSearchPreviewContent();
      case "timeout":
        return renderTimeoutPreviewContent();
      case "back":
        return renderBackButtonPreviewContent();
      default:
        return <Typography>No preview available</Typography>;
    }
  }

  const handleOpenPreview = (type) => {
    setPreviewType(type);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewType("");
  };

  // Render preview device with specific content
  const renderPreviewDevice = (children, fullscreen = false) => (
    <Box
      sx={{
        position: "relative",
        borderRadius: fullscreen ? "0" : "12px",
        width: fullscreen ? "100%" : "100%",
        maxWidth: fullscreen ? "100%" : "450px",
        height: fullscreen ? "1920px" : "800px",
        border: fullscreen ? "none" : "1px solid #e0e0e0",
        boxShadow: fullscreen ? "none" : "0 4px 12px rgba(0,0,0,0.1)",
        backgroundColor: "#ffffff",
        margin: fullscreen ? "0" : "auto",
        transform: fullscreen ? "scale(1)" : "none",
        transformOrigin: "top left",
        ...(fullscreen && {
          position: "absolute",
          top: 0,
          left: 0,
        }),
      }}
    >
      {/* Content Area */}
      <Box
        sx={{
          height: "calc(100% - 72px)",
          position: "relative",
        }}
      >
        {children}
      </Box>
    </Box>
  );

  // ==================== PREVIEW CONTENT RENDERERS ====================

  const renderSearchPreviewContent = () => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        paddingTop: "40px",
      }}
    >
      {/* Back Button Preview */}
      {renderBackButtonPreview()}

      {/* Corner Logos from uploadedUrls */}
      {Object.entries(uploadedUrls).map(([position, logo]) => {
        if (!logo.enabled || !logo.fileUrl) return null;

        return (
          <Box
            key={position}
            sx={{
              position: "absolute",
              left:
                position === "upperLeft" || position === "bottomLeft"
                  ? "5%"
                  : "85%",
              top:
                position === "upperRight" || position === "upperLeft"
                  ? "5%"
                  : "85%",
              zIndex: 10,
            }}
          >
            <img
              src={logo.fileUrl}
              alt={`${position} logo`}
              style={{
                width: `${logo.width || 100}%`,
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        );
      })}

      {/* Center Logo */}
      {settings.searchBox.centerLogo?.enabled &&
        settings.searchBox.centerLogo?.fileUrl && (
          <Box
            sx={{
              // position: 'absolute',
              // top: '50%',
              // left: '50%',
              // transform: 'translate(-50%, -50%)',
              zIndex: 5,
            }}
          >
            <img
              src={settings.searchBox.centerLogo.fileUrl}
              alt="Center logo"
              style={{
                width: `${settings.searchBox.centerLogo.width || 100}%`,
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

      {/* Search Header */}
      {settings.searchBox.headerText && (
        <Box
          sx={{
            marginTop: `${settings.searchBox.headerPositionTop}%`,
            marginBottom: "30px",
            textAlign: "center",
            maxWidth: pxToVw(`${settings.searchBox.width}`),
            padding: "0 20px",
          }}
        >
          <Typography
            sx={{
              fontSize: pxToVw(`${settings.searchBox.headerFontSize}`),
              fontWeight: settings.searchBox.headerFontWeight,
              color: settings.searchBox.headerColor,
              fontFamily: settings.searchBox.headerFontFamily,
              lineHeight: 1.3,
            }}
          >
            {settings.searchBox.headerText}
          </Typography>
        </Box>
      )}

      {/* Search Box */}
      <Box
        sx={{
          position: "relative",
          marginTop: `${settings.searchBox.searchInputPositionTop}%`,
          width: pxToVw(`${settings.searchBox.width}`),
          padding: "0 20px",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder={settings.searchBox.placeholder}
            style={{
              backgroundColor: settings.searchBox.backgroundColor,
              color: settings.searchBox.textColor,
              opacity: settings.searchBox.opacity,
              padding: pxToVw(`${settings.searchBox.padding}`),
              width: pxToVw(`${settings.searchBox.width}`),
              border: `${settings.searchBox.border.width}px solid ${settings.searchBox.border.color}`,
              borderRadius: `${settings.searchBox.border.radius}px`,
              boxShadow: getBoxShadow(settings.searchBox.boxShadow),
              fontSize: pxToVw(`${settings.searchBox.fontSize || 24}`),
              outline: "none",
              fontFamily: "Arial, sans-serif",
            }}
            disabled
          />
          <SearchIcon
            sx={{
              position: "absolute",
              right: "20px",
              color: "#666",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>

      {/* Virtual Keyboard */}
      {renderVirtualKeyboard()}
    </Box>
  );

  const renderTimeoutPreviewContent = () => (
    <Box
      sx={{
        height: "100%",
        position: "relative",
        padding: "20px",
      }}
    >
      {/* Content Placeholder */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "20px",
          height: "100%",
          border: "1px dashed #e0e0e0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#666",
          fontSize: "14px",
        }}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          App is running...
        </Typography>
        <Typography variant="caption" color="text.secondary">
          (After {settings.timeoutPopup.timeoutDuration} seconds of inactivity)
        </Typography>
      </Box>

      {renderTimeoutMainPopupCard()}
    </Box>
  );

  const renderBackButtonPreviewContent = () => (
    <Box
      sx={{
        height: "100%",
        position: "relative",
        backgroundColor: "#f8f9fa",
      }}
    >
      {/* Background content */}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "#666" }}>
          App Content
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This is where your app content would appear
        </Typography>
      </Box>

      {/* Back Button */}
      {renderBackButtonPreview()}
    </Box>
  );

  const renderBackButtonPreview = () => {
    const { backButton } = settings;

    if (backButton.custom === false && backButton.buttonImage.fileUrl) {
      // Render custom image button
      return (
        <Box
          sx={{
            position: "absolute",
            top: `${backButton.position.top}%`,
            left: `${backButton.position.left}%`,
            zIndex: 99,
            transition: "all 0.3s ease",
          }}
        >
          <img
            src={backButton.buttonImage.fileUrl}
            alt="Back Button"
            style={{
              width: pxToVw(backButton.width),
              height: pxToVw(backButton.height),
              cursor: "pointer",
              objectFit: "contain",
            }}
          />
        </Box>
      );
    } else {
      // Render styled button
      return (
        <Box
          sx={{
            position: "absolute",
            top: `${backButton.position.top}%`,
            left: `${backButton.position.left}%`,
            transform: `rotate(${backButton.angle}deg)`,
            transformOrigin: "top left",
            zIndex: 99,
            transition: "all 0.3s ease",
          }}
        >
          <button
            style={{
              color: backButton.textColor,
              backgroundColor: hexToRgba(
                backButton.backgroundColor,
                backButton.opacity,
              ),
              fontSize: pxToVw(`${backButton.fontSize}`),
              fontWeight: backButton.fontWeight,
              padding: `${pxToVw(
                `${backButton.paddingTopBottom || "2"}`,
              )} ${pxToVw(`${backButton.paddingLeftRight || 4}`)}`,
              borderRadius: `${backButton.borderRadius.topLeft}px ${backButton.borderRadius.topRight}px ${backButton.borderRadius.bottomRight}px ${backButton.borderRadius.bottomLeft}px`,
              border: `${backButton.borderWidth}px solid ${backButton.borderColor}`,
              cursor: "pointer",
              boxShadow: getBoxShadow(backButton.shadow),
              letterSpacing: `${backButton.letterSpacing}px`,
              whiteSpace: "nowrap",
              width: pxToVw(`${backButton.width}`),
              height: pxToVw(`${backButton.height}`),
              textAlign: "center",
            }}
          >
            {backButton.text}
          </button>
        </Box>
      );
    }
  };

  const handleKeyboardKey = (key) => {
    let newValue = searchTerm;

    if (key === " ") {
      // Space key
      newValue = searchTerm + " ";
    } else if (key === "backspace") {
      // Backspace key
      newValue = searchTerm.slice(0, -1);
    } else if (key === "shift") {
      // Toggle between lowercase and uppercase
      setKeyboardMode((prev) =>
        prev === "lowercase" ? "uppercase" : "lowercase",
      );
      return;
    } else if (key === "123") {
      // Switch to numeric mode
      setKeyboardMode("numeric");
      return;
    } else if (key === "ABC") {
      // Switch back to letter mode
      setKeyboardMode("lowercase");
      return;
    } else if (key === "#+=") {
      // Switch to special characters mode
      setKeyboardMode("special");
      return;
    } else {
      // Regular character - apply case based on mode
      let char = key;
      if (keyboardMode === "uppercase" && typeof key === "string") {
        char = key.toUpperCase();
      }
      newValue = searchTerm + char;
    }

    setSearchTerm(newValue);
    performSearch(newValue);
  };

  const getKeyStyle = (isSpecialKey = false) => {
    const keyboardSettings = settings?.searchBox?.keyboard || {};

    return {
      width: "100%",
      height: "5vw",
      minHeight: "5vw",
      aspectRatio: "1 / 1",
      backgroundColor: keyboardSettings.keyBackgroundColor || "#ffffff",
      border: `${keyboardSettings.keyBorderWidth || "1"}px solid ${isSpecialKey ? "#5a6268" : keyboardSettings.keyBorderColor || "#e0e0e0"}`,
      borderRadius: `${keyboardSettings.keyBorderRadius || "8"}px`,
      fontSize: "2vw",
      fontWeight: "600",
      color: keyboardSettings.keyTextColor || "#322727",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      boxShadow: getBoxShadow(keyboardSettings.shadow || {}),
      padding: 0,
    };
  };

  const renderTimeoutMainPopupCard = () => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: pxToVw(`${settings.timeoutPopup.popupPadding}` || "24"),
          borderRadius: "12px",
          textAlign: "center",
          width: pxToVw(`${settings.timeoutPopup.popupWidth}` || "320"),
          boxShadow: getBoxShadow(settings.timeoutPopup.button.shadow),
        }}
      >
        <Typography
          sx={{
            color: settings.timeoutPopup.text.color,
            fontSize: pxToVw(`${settings.timeoutPopup.text.fontSize}`),
            fontWeight: settings.timeoutPopup.text.fontWeight,
            fontFamily: settings.timeoutPopup.fontFamily || "Arial, sans-serif",
            marginBottom: "24px",
            lineHeight: 1.4,
          }}
        >
          {getTimeoutPopupContent()}
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            color: settings.timeoutPopup.text.color,
            fontSize: pxToVw(`${settings.timeoutPopup.text.fontSize}`),
            fontWeight: settings.timeoutPopup.text.fontWeight,
            fontFamily: settings.timeoutPopup.fontFamily || "Arial, sans-serif",
            marginBottom: "24px",
            lineHeight: 1.4,
          }}
        >
          {settings.timeoutPopup.timeoutDuration}
        </Typography>
        <button
          style={{
            width: pxToVw(`${settings.timeoutPopup.button.width}` || "2"),
            height: pxToVw(`${settings.timeoutPopup.button.height}` || "1"),
            color: settings.timeoutPopup.button.textColor,
            backgroundColor: settings.timeoutPopup.button.backgroundColor,
            borderRadius: `${settings.timeoutPopup.button.borderRadius}px`,
            padding: pxToVw(`${settings.timeoutPopup.button.padding}`),
            border: "none",
            cursor: "pointer",
            fontSize: pxToVw(`${settings.timeoutPopup.button.fontSize}`),
            fontWeight: 600,
            boxShadow: getBoxShadow(settings.timeoutPopup.button.shadow),
          }}
        >
          {settings.timeoutPopup.button.text}
        </button>
      </Box>
    </Box>
  );

  const renderVirtualKeyboard = () => {
    const keyboardSettings = settings.searchBox.keyboard || {};

    return (
      <div
        style={{
          position: "fixed",
          bottom: `${settings?.searchBox?.keyboardPositionBottom || "2"}%`,
          left: "50%",
          transform: "translateX(-50%)",
          width: "70vw",
          backgroundColor:
            settings?.searchBox?.keyboard?.backgroundColor || "#ffffff",
          borderRadius: `${settings?.searchBox?.keyboard?.borderRadius || "12"}px`,
          border: `${settings?.searchBox?.keyboard?.borderWidth || "1"}px solid ${settings?.searchBox?.keyboard?.borderColor || "#e0e0e0"}`,
          padding: "2vw",
          boxShadow: getBoxShadow(settings?.searchBox?.keyboard?.shadow || {}),
          zIndex: 5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {keyboardMode === "numeric" || keyboardMode === "special" ? (
          // Numeric & Special Characters Keyboard
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.8vw",
                width: "100%",
                margin: "0 auto",
                boxSizing: "border-box",
                height: "100%",
                justifyContent: "center",
              }}
            >
              {/* Row 1 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {(keyboardMode === "numeric"
                  ? ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"]
                  : ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"]
                ).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Row 2 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {(keyboardMode === "numeric"
                  ? ["-", "/", ":", ";", "(", ")", "$", "&", "@", '"']
                  : ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"]
                ).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Row 3 */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr repeat(8, 1fr) 1.5fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() =>
                    handleKeyboardKey(
                      keyboardMode === "numeric" ? "#+=" : "123",
                    )
                  }
                  style={{ ...getKeyStyle(true), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5a6268")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                >
                  {keyboardMode === "numeric" ? "#+=" : "123"}
                </button>
                {(keyboardMode === "numeric"
                  ? [".", ",", "?", "!", "'", '"', "<", ">"]
                  : [":", ";", "'", '"', ",", ".", "<", ">"]
                ).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                  >
                    {key}
                  </button>
                ))}
                <button
                  onClick={() => handleKeyboardKey("backspace")}
                  style={{ ...getKeyStyle(false), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  Del
                </button>
              </div>

              {/* Bottom Control Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 5fr 1.5fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => handleKeyboardKey("ABC")}
                  style={{ ...getKeyStyle(true), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5a6268")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                >
                  ABC
                </button>
                <button
                  onClick={() => handleKeyboardKey(" ")}
                  style={{
                    ...getKeyStyle(false),
                    height: "4.5vw",
                    fontSize: "1.6vw",
                  }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  space
                </button>
                <button
                  // onClick={() => handleKeyboardEnter(searchTerm)}
                  style={{ ...getKeyStyle(false), fontSize: "1.6vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  return
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.9vw",
                width: "100%",
                margin: "0 auto",
                boxSizing: "border-box",
                height: "100%",
                justifyContent: "center",
              }}
            >
              {/* Top Row - Numbers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map(
                  (key) => (
                    <button
                      key={key}
                      onClick={() => handleKeyboardKey(key)}
                      style={getKeyStyle(false)}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f0f0f0")
                      }
                      onMouseUp={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#ffffff")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#ffffff")
                      }
                    >
                      {key}
                    </button>
                  ),
                )}
              </div>

              {/* Second Row - QWERTY */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr) 1.4fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                {["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"].map(
                  (key) => (
                    <button
                      key={key}
                      onClick={() => handleKeyboardKey(key)}
                      style={getKeyStyle(false)}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f0f0f0")
                      }
                      onMouseUp={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#ffffff")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#ffffff")
                      }
                    >
                      {keyboardMode === "uppercase" ? key.toUpperCase() : key}
                    </button>
                  ),
                )}
                <button
                  onClick={() => handleKeyboardKey("backspace")}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  Del
                </button>
              </div>

              {/* Third Row - ASDF */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(9, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                  paddingLeft: "3vw",
                  paddingRight: "3vw",
                }}
              >
                {["a", "s", "d", "f", "g", "h", "j", "k", "l"].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                  >
                    {keyboardMode === "uppercase" ? key.toUpperCase() : key}
                  </button>
                ))}
              </div>

              {/* Fourth Row - ZXCV with Shift */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr repeat(7, 1fr)",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => handleKeyboardKey("shift")}
                  style={{
                    ...getKeyStyle(true),
                    fontSize: "2.5vw",
                    backgroundColor:
                      keyboardMode === "uppercase"
                        ? settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#007bff"
                        : settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#6c757d",
                  }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5a6268")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      keyboardMode === "uppercase"
                        ? settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#007bff"
                        : settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      keyboardMode === "uppercase"
                        ? settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#007bff"
                        : settings?.searchBox?.keyboard?.keyBackgroundColor ||
                          "#6c757d")
                  }
                >
                  ⇧
                </button>
                {["z", "x", "c", "v", "b", "n", "m"].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleKeyboardKey(key)}
                    style={getKeyStyle(false)}
                    onMouseDown={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f0f0f0")
                    }
                    onMouseUp={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        settings?.searchBox?.keyboard?.keyBackgroundColor ||
                        "#ffffff")
                    }
                  >
                    {keyboardMode === "uppercase" ? key.toUpperCase() : key}
                  </button>
                ))}
              </div>

              {/* Bottom Control Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 3.5fr 1.3fr",
                  gap: "0.6vw",
                  width: "100%",
                }}
              >
                <button
                  onClick={() => handleKeyboardKey("123")}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  123
                </button>
                <button
                  onClick={() => handleKeyboardKey(" ")}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  space
                </button>
                <button
                  onClick={() => handleKeyboardEnter(searchTerm)}
                  style={{ ...getKeyStyle(false), fontSize: "1.8vw" }}
                  onMouseDown={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f0f0f0")
                  }
                  onMouseUp={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      settings?.searchBox?.keyboard?.keyBackgroundColor ||
                      "#ffffff")
                  }
                >
                  return
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ==================== SEARCH BOX TAB ====================
  const renderSearchBoxTab = () => (
    <Box>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className=" " style={{ padding: "0", marginBottom: "20px" }}>
            <div
              className=""
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <div>
                <div className="title">
                  <Typography variant="h4">Search Page Settings</Typography>
                </div>
              </div>
              <div>
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenPreview("search")}
                  style={{
                    backgroundColor: buttonColor,
                    borderColor: buttonColor,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <PreviewIcon /> Open Full Preview
                </button>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            <div className="tab-wizard wizard-circle wizard vertical">
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  bgcolor: `${buttonColor}10`,
                  border: "1px solid",
                  borderColor: `${buttonColor}30`,
                  marginBottom: "20px",
                }}
              >
                Customize the search page appearance
              </Alert>

              {/* Preview Section */}
              <div className="form-group  ">
                <div
                  style={{
                    display: "flex",

                    padding: "80px",
                    overflow: "auto",
                    // backgroundColor: '#f5f5f5',
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {/* Corner Logos */}
                    {/* {Object.entries(uploadedUrls).map(([position, logo]) => {
                      if (!logo.enabled || !logo.fileUrl) return null;
                      return (
                        <div
                          key={position}
                          style={{
                            position: 'absolute',
                            left:
                              position === 'upperLeft' || position === 'bottomLeft' ? '5%' : '85%',
                            top:
                              position === 'upperRight' || position === 'upperLeft' ? '5%' : '85%',
                            zIndex: 10,
                          }}
                        >
                          <img
                            src={logo.fileUrl}
                            alt={`${position} logo`}
                            style={{
                              width: `${logo.width || 100}%`,
                              height: 'auto',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      );
                    })} */}

                    {/* Search Header */}
                    {settings.searchBox.headerText && (
                      <div
                        style={{
                          marginTop: `${settings.searchBox.headerPositionTop}%`,
                          marginBottom: "30px",
                          textAlign: "center",
                          maxWidth: pxToVw(`${settings.searchBox.width}`),
                        }}
                      >
                        <div
                          style={{
                            fontSize: pxToVw(
                              `${settings.searchBox.headerFontSize}`,
                            ),
                            fontWeight: settings.searchBox.headerFontWeight,
                            color: settings.searchBox.headerColor,
                            fontFamily: settings.searchBox.headerFontFamily,
                            lineHeight: 1.3,
                          }}
                        >
                          {settings.searchBox.headerText}
                        </div>
                      </div>
                    )}

                    {/* Search Box */}
                    <div
                      style={{
                        position: "relative",
                        marginTop: `${settings.searchBox.searchInputPositionTop}%`,
                        marginBottom: "40px",
                        width: pxToVw(`${settings.searchBox.width}`),
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          placeholder={settings.searchBox.placeholder}
                          style={{
                            backgroundColor: settings.searchBox.backgroundColor,
                            color: settings.searchBox.textColor,
                            opacity: settings.searchBox.opacity,
                            padding: pxToVw(`${settings.searchBox.padding}`),
                            width: "100%",
                            border: `${settings.searchBox.border.width}px solid ${settings.searchBox.border.color}`,
                            borderRadius: `${settings.searchBox.border.radius}px`,
                            boxShadow: getBoxShadow(
                              settings.searchBox.boxShadow,
                            ),
                            fontSize: pxToVw(
                              `${settings.searchBox.fontSize || 24}`,
                            ),
                            outline: "none",
                          }}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Basic Settings */}
              <div className="form-group  ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography
                    variant="h6"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Settings />
                    Basic Settings
                  </Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Header Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.searchBox.headerText}
                        onChange={(e) =>
                          updateSearchBox("headerText", e.target.value)
                        }
                        placeholder="Leave empty to hide header"
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Header Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.headerFontSize}
                        onChange={(e) =>
                          updateSearchBox(
                            "headerFontSize",
                            parseInt(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Header Position from Top (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.headerPositionTop}
                        onChange={(e) =>
                          updateSearchBoxPosition(
                            "headerPositionTop",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <>
                  <div className="row menu-card2">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Header Font Family</label>
                        <select
                          className="form-control"
                          value={settings.searchBox.headerFontFamily}
                          onChange={(e) =>
                            updateSearchBox("headerFontFamily", e.target.value)
                          }
                        >
                          {fontOptions}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Header Font Weight</label>
                        <select
                          className="form-control"
                          value={settings.searchBox.headerFontWeight}
                          onChange={(e) =>
                            updateSearchBox(
                              "headerFontWeight",
                              parseInt(e.target.value),
                            )
                          }
                        >
                          <option value={100}>100 (Thin)</option>
                          <option value={200}>200 (Extra Light)</option>
                          <option value={300}>300 (Light)</option>
                          <option value={400}>400 (Normal)</option>
                          <option value={500}>500 (Medium)</option>
                          <option value={600}>600 (Semi Bold)</option>
                          <option value={700}>700 (Bold)</option>
                          <option value={800}>800 (Extra Bold)</option>
                          <option value={900}>900 (Black)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row menu-card2">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Header Color</label>
                        <MuiColorInput
                          format="hex"
                          value={settings.searchBox.headerColor}
                          onChange={(color) =>
                            updateSearchBox("headerColor", color)
                          }
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px",
                              height: 53,
                            },
                            "& .MuiOutlinedInput-input": {
                              height: "36px",
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Search Input Color</label>
                        <MuiColorInput
                          format="hex"
                          value={settings.searchBox.backgroundColor}
                          onChange={(color) =>
                            updateSearchBox("backgroundColor", color)
                          }
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px",
                              height: 53,
                            },
                            "& .MuiOutlinedInput-input": {
                              height: "36px",
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Search Input Text Color</label>
                        <MuiColorInput
                          format="hex"
                          value={settings.searchBox.textColor}
                          onChange={(color) =>
                            updateSearchBox("textColor", color)
                          }
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0px",
                              height: 53,
                            },
                            "& .MuiOutlinedInput-input": {
                              height: "36px",
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Placeholder Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.searchBox.placeholder}
                        onChange={(e) =>
                          updateSearchBox("placeholder", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.fontSize}
                        onChange={(e) =>
                          updateSearchBox("fontSize", parseInt(e.target.value))
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.width}
                        onChange={(e) =>
                          updateSearchBox("width", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>
                        Opacity: {Math.round(settings.searchBox.opacity * 100)}%
                      </label>
                      <Slider
                        value={settings.searchBox.opacity}
                        onChange={(e, value) =>
                          updateSearchBox("opacity", value)
                        }
                        min={0.1}
                        max={1}
                        step={0.1}
                        valueLabelDisplay="auto"
                        sx={{ color: buttonColor }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Padding (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.padding}
                        onChange={(e) =>
                          updateSearchBox("padding", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Spacing Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Spacing Settings</Typography>
                </div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  style={{ marginBottom: "20px" }}
                >
                  Control spacing between location buttons and keyboard
                </Typography>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Margin Between Buttons (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.buttonMargin || "20"}
                        onChange={(e) =>
                          updateSearchBox("buttonMargin", e.target.value)
                        }
                        placeholder="Margin between location buttons"
                      />
                      <small className="form-text text-muted">
                        Space/gap between location buttons in the grid
                      </small>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Margin Top of Buttons (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.buttonMarginTop || "40"}
                        onChange={(e) =>
                          updateSearchBox("buttonMarginTop", e.target.value)
                        }
                        placeholder="Top margin for button grid"
                      />
                      <small className="form-text text-muted">
                        Distance from search box to location buttons
                      </small>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Keyboard Margin Top from Buttons (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboardPositionBottom || "25"
                        }
                        onChange={(e) =>
                          updateSearchBox(
                            "keyboardPositionBottom",
                            e.target.value,
                          )
                        }
                        placeholder="Keyboard Margin Top from Buttons"
                      />
                      <small className="form-text text-muted">
                        Position of keyboard from top of buttons 
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wheelchair Accessibility Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Accessibility Settings</Typography>
                </div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  style={{ marginBottom: "20px" }}
                >
                  Adjust content positioning for wheelchair accessibility mode
                </Typography>

                <div className="row menu-card2">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label style={{ fontWeight: "normal" }}>
                        Wheelchair Mode Content Top Margin (%):
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.wheelchairContentOffsetY || 20}
                        onChange={(e) =>
                          updateSearchBox("wheelchairContentOffsetY", Number(e.target.value))
                        }
                        min="0"
                        max="50"
                      />
                      <small className="form-text text-muted" style={{ display: 'block', marginTop: '8px' }}>
                        Content will move down by this percentage when wheelchair mode is active (controlled by global toggle in System Settings)
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Border Settings</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.border.width}
                        onChange={(e) =>
                          updateSearchBoxBorder("width", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.searchBox.border.color}
                        onChange={(color) =>
                          updateSearchBoxBorder("color", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.border.radius}
                        onChange={(e) =>
                          updateSearchBoxBorder("radius", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Box Shadow Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Box Shadow</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Shadow Angle</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.boxShadow.shadowAngle}
                        onChange={(e) =>
                          updateSearchBoxShadow("shadowAngle", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Shadow Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.boxShadow.shadowSize}
                        onChange={(e) =>
                          updateSearchBoxShadow("shadowSize", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Shadow Distance</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.boxShadow.shadowDistance}
                        onChange={(e) =>
                          updateSearchBoxShadow(
                            "shadowDistance",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.searchBox.boxShadow.shadowColor}
                        onChange={(color) =>
                          updateSearchBoxShadow("shadowColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Alpha</label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.1"
                        min="0"
                        max="1"
                        value={settings.searchBox.boxShadow.shadowAlpha}
                        onChange={(e) =>
                          updateSearchBoxShadow(
                            "shadowAlpha",
                            parseFloat(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Virtual Keyboard Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">
                    Virtual Keyboard Settings
                  </Typography>
                </div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  style={{ marginBottom: "20px" }}
                >
                  Customize the appearance of the virtual keyboard
                </Typography>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Keyboard Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.searchBox.keyboard?.backgroundColor ||
                          "#ffffff"
                        }
                        onChange={(color) =>
                          updateKeyboard("backgroundColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Keyboard Border Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.searchBox.keyboard?.borderWidth || "1"}
                        onChange={(e) =>
                          updateKeyboard("borderWidth", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Keyboard Border Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.searchBox.keyboard?.borderColor || "#e0e0e0"
                        }
                        onChange={(color) =>
                          updateKeyboard("borderColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Keyboard Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboard?.borderRadius || "12"
                        }
                        onChange={(e) =>
                          updateKeyboard("borderRadius", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Key Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.searchBox.keyboard?.keyBackgroundColor ||
                          "#ffffff"
                        }
                        onChange={(color) =>
                          updateKeyboard("keyBackgroundColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Key Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.searchBox.keyboard?.keyTextColor || "#322727"
                        }
                        onChange={(color) =>
                          updateKeyboard("keyTextColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Key Border Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboard?.keyBorderWidth || "1"
                        }
                        onChange={(e) =>
                          updateKeyboard("keyBorderWidth", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Key Border Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.searchBox.keyboard?.keyBorderColor ||
                          "#e0e0e0"
                        }
                        onChange={(color) =>
                          updateKeyboard("keyBorderColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Key Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboard?.keyBorderRadius || "8"
                        }
                        onChange={(e) =>
                          updateKeyboard("keyBorderRadius", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Keyboard Shadow Settings */}
                <div
                  className="form-group"
                  style={{ marginTop: "20px", marginBottom: "15px" }}
                >
                  <Typography variant="subtitle1">Keyboard Shadow</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Shadow Angle</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboard?.shadow?.shadowAngle || 45
                        }
                        onChange={(e) =>
                          updateKeyboardShadow("shadowAngle", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Shadow Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboard?.shadow?.shadowSize || 5
                        }
                        onChange={(e) =>
                          updateKeyboardShadow("shadowSize", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Shadow Distance</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.searchBox.keyboard?.shadow?.shadowDistance ||
                          2
                        }
                        onChange={(e) =>
                          updateKeyboardShadow("shadowDistance", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.searchBox.keyboard?.shadow?.shadowColor ||
                          "#000000"
                        }
                        onChange={(color) =>
                          updateKeyboardShadow("shadowColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Alpha</label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.1"
                        min="0"
                        max="1"
                        value={
                          settings.searchBox.keyboard?.shadow?.shadowAlpha ||
                          0.15
                        }
                        onChange={(e) =>
                          updateKeyboardShadow(
                            "shadowAlpha",
                            parseFloat(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Corner Logos */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Search Page Media</Typography>
                </div>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  style={{ marginBottom: "20px" }}
                >
                  Add logos to the search screen
                </Typography>

                <MainWayfinderMedia
                  type="search"
                  userId={userId}
                  buttonColor={buttonColor}
                  title="Search Screen Logos"
                  uploadedUrls={uploadedUrls}
                  setUploadedUrls={setUploadedUrls}
                  uploadingStates={uploadingStates}
                  setUploadingStates={setUploadingStates}
                  fetchCategoryData={fetchCategoryData}
                  loading={isLoading}
                  handleUpload={handleUpload}
                  handleDeleteLogo={handleDeleteLogo}
                  handleSave={handleSave}
                  handleToggleChangeMedia={handleToggleChangeMedia}
                  showSaveBtn={false}
                />

                <div
                  className="form-group"
                  style={{ padding: "20px", paddingTop: "0" }}
                >
                  <div className="row  ">
                    <div className="col-md-6 d-flex p-3">
                      <div className="form-group menu-card2 p-3 h-100 w-100">
                        {/* <div className="form-group">
                          <label>Center Logo Image:</label>
                        </div>
                        <div className="form-group">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={settings.searchBox.centerLogo?.enabled || false}
                                onChange={e =>
                                  updateSearchBox('centerLogo', {
                                    ...settings.searchBox.centerLogo,
                                    enabled: e.target.checked,
                                  })
                                }
                                color="primary"
                              />
                            }
                            label="Enable Center Logo"
                          />
                        </div> */}

                        <div className="form-group">
                          <label>Center Logo:</label>
                          <ToggleSwitch
                            label="Enable"
                            isOn={
                              settings.searchBox.centerLogo?.enabled || false
                            }
                            id={"centerLogo"}
                            onChange={(val) => handleCenterLogoUpdate(val)}
                          />
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "5px",
                          }}
                        >
                          <MediaDropzone
                            onDrop={(acceptedFiles) => {
                              if (acceptedFiles.length > 0) {
                                setSelectedCenterLogoFile(acceptedFiles[0]);
                              }
                            }}
                            selectedFile={selectedCenterLogoFile}
                          />

                          <UploadedUrlImage
                            url={settings.searchBox.centerLogo}
                          />
                        </div>

                        <div>
                          <UploadedUrl
                            url={settings.searchBox.centerLogo}
                            uploadedUrls={settings.searchBox.centerLogo}
                            onDelete={handleRemoveCenterLogo}
                            buttonColor={buttonColor}
                            handleDeleteLogo={handleRemoveCenterLogo}
                            logoState="centerLogo"
                          />
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            alignItems: "center",
                            marginTop: "20px",
                          }}
                        >
                          <UploadButton
                            uploading={uploading}
                            onUpload={() => {
                              if (selectedCenterLogoFile) {
                                handleCenterLogoUpload(selectedCenterLogoFile);
                              } else if (
                                settings.searchBox.centerLogo?.fileUrl
                              ) {
                                showErrorToast(
                                  "Image already uploaded. Remove it first to upload a new one.",
                                );
                              } else {
                                showErrorToast("Please select a file first");
                              }
                            }}
                            buttonColor={buttonColor}
                            style={{ margin: "0" }}
                            disabled={
                              uploading ||
                              (!selectedCenterLogoFile &&
                                !settings.searchBox.centerLogo?.fileUrl)
                            }
                          />

                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              alignItems: "center",
                              width: "40%",
                              justifyContent: "flex-end",
                            }}
                          >
                            <label htmlFor="">Size (%):</label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Width"
                              value={
                                settings.searchBox.centerLogo?.width || "100"
                              }
                              onChange={(e) =>
                                updateSearchBox("centerLogo", {
                                  ...settings.searchBox.centerLogo,
                                  width: e.target.value,
                                })
                              }
                              style={{ width: "50%" }}
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            marginTop: "15px",
                          }}
                        >
                          <label htmlFor="" style={{ minWidth: "180px" }}>
                            Position from Top (%):
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="10"
                            value={
                              settings.searchBox.centerLogo?.positionTop || "10"
                            }
                            onChange={(e) =>
                              updateSearchBox("centerLogo", {
                                ...settings.searchBox.centerLogo,
                                positionTop: e.target.value,
                              })
                            }
                            style={{ width: "100px" }}
                          />
                          <small className="form-text text-muted">
                            Distance from top of screen (percentage)
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );

  // ==================== TIMEOUT POPUP TAB ====================
  const renderTimeoutPopupTab = () => (
    <Box>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="" style={{ padding: "0", marginBottom: "20px" }}>
            <div
              className=" "
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <div>
                <div className="title">
                  <Typography variant="h4">Timeout Popup Settings</Typography>
                </div>
              </div>
              <div>
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenPreview("timeout")}
                  style={{
                    backgroundColor: buttonColor,
                    borderColor: buttonColor,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <PreviewIcon /> Open Full Preview
                </button>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            <div className="tab-wizard wizard-circle wizard vertical">
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  bgcolor: `${buttonColor}10`,
                  border: "1px solid",
                  borderColor: `${buttonColor}30`,
                  marginBottom: "20px",
                }}
              >
                Configure timeout popup settings for user inactivity
              </Alert>

              {/* Preview Section */}
              <div className="form-group menu-card2">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography
                    variant="h6"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <AccessTime />
                    Timeout Popup Preview
                  </Typography>
                </div>

                <div
                  style={{
                    height: "60vh",
                    position: "relative",
                    padding: "20px",
                    overflow: "auto",
                    // backgroundColor: '#f5f5f5',
                    borderRadius: "8px",
                  }}
                >
                  {renderTimeoutMainPopupCard()}
                </div>
              </div>

              {/* Basic Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography
                    variant="h6"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Settings />
                    Basic Settings
                  </Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Timeout Duration (seconds)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.timeoutDuration}
                        onChange={(e) =>
                          updateTimeoutPopup(
                            "timeoutDuration",
                            parseInt(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Message Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={getTimeoutPopupContent()}
                        onChange={(e) =>
                          updateTimeoutText("content", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.text.color}
                        onChange={(color) => updateTimeoutText("color", color)}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Font Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.text.fontSize}
                        onChange={(e) =>
                          updateTimeoutText(
                            "fontSize",
                            parseInt(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Font Weight</label>
                      <select
                        className="form-control"
                        value={settings.timeoutPopup.text.fontWeight}
                        onChange={(e) =>
                          updateTimeoutText(
                            "fontWeight",
                            parseInt(e.target.value),
                          )
                        }
                      >
                        <option value={100}>100 (Thin)</option>
                        <option value={200}>200 (Extra Light)</option>
                        <option value={300}>300 (Light)</option>
                        <option value={400}>400 (Normal)</option>
                        <option value={500}>500 (Medium)</option>
                        <option value={600}>600 (Semi Bold)</option>
                        <option value={700}>700 (Bold)</option>
                        <option value={800}>800 (Extra Bold)</option>
                        <option value={900}>900 (Black)</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Font Family</label>
                      <select
                        className="form-control"
                        value={settings.timeoutPopup.fontFamily}
                        onChange={(e) =>
                          updateTimeoutPopup("fontFamily", e.target.value)
                        }
                      >
                        {fontOptions}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Popup Width</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.popupWidth}
                        onChange={(e) =>
                          updateTimeoutPopup(
                            "popupWidth",
                            parseInt(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Padding</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.popupPadding}
                        onChange={(e) =>
                          updateTimeoutPopup(
                            "popupPadding",
                            parseInt(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Button Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Button Settings</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Button Text</label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.timeoutPopup.button.text}
                        onChange={(e) =>
                          updateTimeoutButton("text", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.button.textColor}
                        onChange={(color) =>
                          updateTimeoutButton("textColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.button.backgroundColor}
                        fullWidth
                        onChange={(color) =>
                          updateTimeoutButton("backgroundColor", color)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.borderRadius}
                        onChange={(e) =>
                          updateTimeoutButton("borderRadius", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Font Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.fontSize}
                        onChange={(e) =>
                          updateTimeoutButton("fontSize", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Padding</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.padding}
                        onChange={(e) =>
                          updateTimeoutButton("padding", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Width</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.width}
                        onChange={(e) =>
                          updateTimeoutButton("width", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Height</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.height}
                        onChange={(e) =>
                          updateTimeoutButton("height", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Button Shadow */}
              <div className="form-group">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Button Shadow</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Angle</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.shadow.shadowAngle}
                        onChange={(e) =>
                          updateTimeoutButtonShadow(
                            "shadowAngle",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.button.shadow.shadowSize}
                        onChange={(e) =>
                          updateTimeoutButtonShadow(
                            "shadowSize",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Distance</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.timeoutPopup.button.shadow.shadowDistance
                        }
                        onChange={(e) =>
                          updateTimeoutButtonShadow(
                            "shadowDistance",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.button.shadow.shadowColor}
                        fullWidth
                        onChange={(color) =>
                          updateTimeoutButtonShadow("shadowColor", color)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Shadow Alpha</label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.1"
                        min="0"
                        max="1"
                        value={settings.timeoutPopup.button.shadow.shadowAlpha}
                        onChange={(e) =>
                          updateTimeoutButtonShadow(
                            "shadowAlpha",
                            parseFloat(e.target.value),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Avatar Screen Settings */}
              <div className="form-group">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography
                    variant="h6"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Settings />
                    Avatar Screen Settings
                  </Typography>
                </div>

                {/* Header Text */}
                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>
                        Header Text
                        {availableLanguages.length > 1 && (
                          <Chip
                            label={`Editing: ${getLanguageName(currentLanguage)}`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: buttonColor,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.headerText || "What would you like to do next?"}
                        onChange={(e) =>
                          updateAvatarScreen("headerText", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Countdown Duration (seconds)</label>
                      <input
                        type="number"
                        className="form-control"
                        min="5"
                        max="300"
                        value={settings.timeoutPopup.avatarScreen?.countdownDuration || 30}
                        onChange={(e) =>
                          updateAvatarScreen("countdownDuration", parseInt(e.target.value) || 30)
                        }
                      />
                      <small className="form-text text-muted">
                        Time before automatically closing the window (5-300 seconds)
                      </small>
                    </div>
                  </div>
                </div>

                {/* Replay Button Settings */}
                <div className="form-group" style={{ marginTop: "20px", marginBottom: "15px" }}>
                  <Typography variant="subtitle1">Replay Button Settings</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Button Text
                        {availableLanguages.length > 1 && (
                          <Chip
                            label={`Editing: ${getLanguageName(currentLanguage)}`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: buttonColor,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.replayButton?.text || "Replay Directions"}
                        onChange={(e) =>
                          updateAvatarScreenButton("replayButton", "text", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.avatarScreen?.replayButton?.backgroundColor || "#000000"}
                        onChange={(color) =>
                          updateAvatarScreenButton("replayButton", "backgroundColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.avatarScreen?.replayButton?.textColor || "#ffffff"}
                        onChange={(color) =>
                          updateAvatarScreenButton("replayButton", "textColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.replayButton?.fontSize || "16"}
                        onChange={(e) =>
                          updateAvatarScreenButton("replayButton", "fontSize", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Padding (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.replayButton?.padding || "20"}
                        onChange={(e) =>
                          updateAvatarScreenButton("replayButton", "padding", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.replayButton?.borderRadius || "8"}
                        onChange={(e) =>
                          updateAvatarScreenButton("replayButton", "borderRadius", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Back to Search Button Settings */}
                <div className="form-group" style={{ marginTop: "20px", marginBottom: "15px" }}>
                  <Typography variant="subtitle1">Back to Search Button Settings</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Button Text
                        {availableLanguages.length > 1 && (
                          <Chip
                            label={`Editing: ${getLanguageName(currentLanguage)}`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: buttonColor,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.backToSearchButton?.text || "Back to Search"}
                        onChange={(e) =>
                          updateAvatarScreenButton("backToSearchButton", "text", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.avatarScreen?.backToSearchButton?.backgroundColor || "#666666"}
                        onChange={(color) =>
                          updateAvatarScreenButton("backToSearchButton", "backgroundColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.avatarScreen?.backToSearchButton?.textColor || "#ffffff"}
                        onChange={(color) =>
                          updateAvatarScreenButton("backToSearchButton", "textColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.backToSearchButton?.fontSize || "16"}
                        onChange={(e) =>
                          updateAvatarScreenButton("backToSearchButton", "fontSize", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Padding (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.backToSearchButton?.padding || "20"}
                        onChange={(e) =>
                          updateAvatarScreenButton("backToSearchButton", "padding", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.backToSearchButton?.borderRadius || "8"}
                        onChange={(e) =>
                          updateAvatarScreenButton("backToSearchButton", "borderRadius", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Close Window Button Settings */}
                <div className="form-group" style={{ marginTop: "20px", marginBottom: "15px" }}>
                  <Typography variant="subtitle1">Close Window Button Settings</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>
                        Button Text
                        {availableLanguages.length > 1 && (
                          <Chip
                            label={`Editing: ${getLanguageName(currentLanguage)}`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: buttonColor,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.closeWindowButton?.text || "Close this Window"}
                        onChange={(e) =>
                          updateAvatarScreenButton("closeWindowButton", "text", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.avatarScreen?.closeWindowButton?.backgroundColor || "#999999"}
                        onChange={(color) =>
                          updateAvatarScreenButton("closeWindowButton", "backgroundColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.timeoutPopup.avatarScreen?.closeWindowButton?.textColor || "#ffffff"}
                        onChange={(color) =>
                          updateAvatarScreenButton("closeWindowButton", "textColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.closeWindowButton?.fontSize || "16"}
                        onChange={(e) =>
                          updateAvatarScreenButton("closeWindowButton", "fontSize", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Padding (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.closeWindowButton?.padding || "20"}
                        onChange={(e) =>
                          updateAvatarScreenButton("closeWindowButton", "padding", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.timeoutPopup.avatarScreen?.closeWindowButton?.borderRadius || "8"}
                        onChange={(e) =>
                          updateAvatarScreenButton("closeWindowButton", "borderRadius", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  );

  // ==================== BACK BUTTON TAB ====================
  const renderBackButtonTab = () => (
    <Box>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className=" " style={{ padding: "0", marginBottom: "20px" }}>
            <div
              className=" "
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <div>
                <div className="title">
                  <Typography variant="h4">Back Button Settings</Typography>
                </div>
              </div>
              <div>
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenPreview("back")}
                  style={{
                    backgroundColor: buttonColor,
                    borderColor: buttonColor,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <PreviewIcon /> Open Full Preview
                </button>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            <div className="tab-wizard wizard-circle wizard vertical">
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  bgcolor: `${buttonColor}10`,
                  border: "1px solid",
                  borderColor: `${buttonColor}30`,
                  marginBottom: "20px",
                }}
              >
                Customize the back button appearance and position
              </Alert>

              {/* Preview Section */}
              {settings.backButton.custom && (
                <div className="form-group menu-card2">
                  <div className="form-group" style={{ marginBottom: "15px" }}>
                    <Typography
                      variant="h6"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <ArrowBack />
                      Back Button Preview
                    </Typography>
                  </div>

                  <div
                    style={{
                      height: "60vh",
                      position: "relative",
                      padding: "20px",
                      overflow: "auto",
                      // backgroundColor: '#f5f5f5',
                      borderRadius: "8px",
                    }}
                  >
                    {renderBackButtonPreviewContent()}
                  </div>
                </div>
              )}

              {/* Basic Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography
                    variant="h6"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Settings />
                    Basic Settings
                  </Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-12">
                    <div className="form-group">
                      <FormControlLabel
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        control={
                          <Switch
                            checked={settings.backButton.custom === false}
                            onChange={(e) =>
                              updateBackButton("custom", !e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={
                          <>
                            <span style={{ fontWeight: "600" }}>
                              Upload Back Button Image
                            </span>
                          </>
                        }
                      />
                    </div>
                  </div>
                </div>

                {settings.backButton.custom === false ? (
                  <>
                    <div className="row menu-card2">
                      <div className="col-md-6 d-flex p-3">
                        <div className="form-group menu-card2 p-3 h-100 w-100">
                          <div className="form-group">
                            <label>Back Button Image:</label>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "5px",
                            }}
                          >
                            <MediaDropzone
                              onDrop={(acceptedFiles) => {
                                if (acceptedFiles.length > 0) {
                                  setSelectedBackButtonFile(acceptedFiles[0]);
                                }
                              }}
                              selectedFile={selectedBackButtonFile}
                            />

                            <UploadedUrlImage
                              url={settings.backButton?.buttonImage}
                            />
                          </div>

                          <div>
                            <UploadedUrl
                              url={settings.backButton?.buttonImage}
                              uploadedUrls={settings.backButton?.buttonImage}
                              onDelete={handleRemoveImage}
                              buttonColor={buttonColor}
                              handleDeleteLogo={() => handleRemoveImage()}
                              logoState="backButton"
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "10px",
                              alignItems: "center",
                              marginTop: "20px",
                            }}
                          >
                            <UploadButton
                              uploading={uploading}
                              onUpload={() => {
                                if (selectedBackButtonFile) {
                                  handleImageUpload([selectedBackButtonFile]);
                                } else if (
                                  settings.backButton?.buttonImage?.fileUrl
                                ) {
                                  showErrorToast(
                                    "Image already uploaded. Remove it first to upload a new one.",
                                  );
                                } else {
                                  showErrorToast("Please select a file first");
                                }
                              }}
                              buttonColor={buttonColor}
                              style={{ margin: "0" }}
                              disabled={
                                uploading ||
                                (!selectedBackButtonFile &&
                                  !settings.backButton?.buttonImage?.fileUrl)
                              }
                            />

                            <div
                              style={{
                                display: "flex",
                                gap: "10px",
                                alignItems: "center",
                                width: "40%",
                                justifyContent: "flex-end",
                              }}
                            >
                              <label htmlFor="">Size (%):</label>
                              <input
                                type="number"
                                className="form-control"
                                placeholder="Width"
                                value={
                                  settings.backButton?.buttonImage?.width ||
                                  "50"
                                }
                                onChange={(e) =>
                                  updateBackButton("buttonImage", {
                                    ...settings.backButton.buttonImage,
                                    width: e.target.value,
                                  })
                                }
                                style={{ width: "50%" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* <div className="row menu-card2">
                      <div className="col-md-12 d-flex p-3">
                        <div
                          className="form-group"
                          style={{
                            width: "100%",
                            display: "flex",
                            gap: "10px",
                            alignItems: "flex-end",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <label>Upload Status</label>
                            {uploading ? (
                              <Box
                                sx={{
                                  padding: "10px 15px",
                                  backgroundColor: "#e3f2fd",
                                  borderRadius: "5px",
                                  color: buttonColor,
                                }}
                              >
                                <Typography variant="body2">
                                  Uploading...
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  padding: "10px 15px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "5px",
                                }}
                              >
                                <Typography variant="body2">
                                  {settings.backButton?.buttonImage?.fileUrl
                                    ? "Image uploaded"
                                    : selectedBackButtonFile
                                      ? "File selected, click 'Upload' to upload"
                                      : "Ready to upload image"}
                                </Typography>
                              </Box>
                            )}
                          </div>
                        </div>
                      </div>
                    </div> */}
                  </>
                ) : (
                  <>
                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Button Text</label>
                          <input
                            type="text"
                            className="form-control"
                            value={settings.backButton.text}
                            onChange={(e) =>
                              updateBackButton("text", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Text Color</label>
                          <MuiColorInput
                            format="hex"
                            value={settings.backButton.textColor}
                            onChange={(color) =>
                              updateBackButton("textColor", color)
                            }
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "0px",
                                height: 55,
                              },
                              "& .MuiOutlinedInput-input": {
                                height: "36px",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Background Color</label>
                          <MuiColorInput
                            format="hex"
                            value={settings.backButton.backgroundColor}
                            onChange={(color) =>
                              updateBackButton("backgroundColor", color)
                            }
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "0px",
                                height: 53,
                              },
                              "& .MuiOutlinedInput-input": {
                                height: "36px",
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>
                            Opacity:{" "}
                            {Math.round(settings.backButton.opacity * 100)}%
                          </label>
                          <Slider
                            value={settings.backButton.opacity}
                            onChange={(e, value) =>
                              updateBackButton("opacity", value)
                            }
                            min={0.1}
                            max={1}
                            step={0.1}
                            valueLabelDisplay="auto"
                            sx={{ color: buttonColor }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Font Size</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.fontSize}
                            onChange={(e) =>
                              updateBackButton(
                                "fontSize",
                                parseInt(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Font Weight</label>
                          <select
                            className="form-control"
                            value={settings.backButton.fontWeight}
                            onChange={(e) =>
                              updateBackButton(
                                "fontWeight",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            <option value={100}>100 (Thin)</option>
                            <option value={200}>200 (Extra Light)</option>
                            <option value={300}>300 (Light)</option>
                            <option value={400}>400 (Normal)</option>
                            <option value={500}>500 (Medium)</option>
                            <option value={600}>600 (Semi Bold)</option>
                            <option value={700}>700 (Bold)</option>
                            <option value={800}>800 (Extra Bold)</option>
                            <option value={900}>900 (Black)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Rotation Angle</label>
                          <select
                            className="form-control"
                            value={settings.backButton.angle}
                            onChange={(e) =>
                              updateBackButton("angle", Number(e.target.value))
                            }
                          >
                            <option value={0}>0° (Horizontal)</option>
                            <option value={90}>90° (Vertical)</option>
                            <option value={180}>180° (Upside Down)</option>
                            <option value={270}>
                              270° (Vertical Inverted)
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Letter Spacing (px)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.letterSpacing}
                            onChange={(e) =>
                              updateBackButton(
                                "letterSpacing",
                                Number(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Padding Top/Bottom</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.paddingTopBottom}
                            onChange={(e) =>
                              updateBackButton(
                                "paddingTopBottom",
                                parseInt(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Padding Left/Right</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.paddingLeftRight}
                            onChange={(e) =>
                              updateBackButton(
                                "paddingLeftRight",
                                parseInt(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Width</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.width}
                            onChange={(e) =>
                              updateBackButton("width", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Height</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.height}
                            onChange={(e) =>
                              updateBackButton("height", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Position Settings */}
              <div className="form-group ">
                <div className="form-group" style={{ marginBottom: "15px" }}>
                  <Typography variant="h6">Position Settings</Typography>
                </div>

                <div className="row menu-card2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Top (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.backButton.position.top}
                        onChange={(e) =>
                          updateBackButtonPosition("top", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Left (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.backButton.position.left}
                        onChange={(e) =>
                          updateBackButtonPosition("left", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Styled Button Specific Settings */}
              {settings.backButton.custom === true && (
                <>
                  {/* Border Settings */}
                  <div className="form-group ">
                    <div
                      className="form-group"
                      style={{ marginBottom: "15px" }}
                    >
                      <Typography variant="h6">Border Settings</Typography>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Border Width</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.borderWidth}
                            onChange={(e) =>
                              updateBackButton("borderWidth", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Border Color</label>
                          <MuiColorInput
                            format="hex"
                            value={settings.backButton.borderColor}
                            onChange={(color) =>
                              updateBackButton("borderColor", color)
                            }
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "0px",
                                height: 53,
                              },
                              "& .MuiOutlinedInput-input": {
                                height: "36px",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Top Left Radius (px)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.borderRadius.topLeft}
                            onChange={(e) =>
                              updateBackButtonRadius("topLeft", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Top Right Radius (px)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.borderRadius.topRight}
                            onChange={(e) =>
                              updateBackButtonRadius("topRight", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Bottom Left Radius (px)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.borderRadius.bottomLeft}
                            onChange={(e) =>
                              updateBackButtonRadius(
                                "bottomLeft",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Bottom Right Radius (px)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.borderRadius.bottomRight}
                            onChange={(e) =>
                              updateBackButtonRadius(
                                "bottomRight",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Shadow Settings */}
                  <div className="form-group">
                    <div
                      className="form-group"
                      style={{ marginBottom: "15px" }}
                    >
                      <Typography variant="h6">Shadow Settings</Typography>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Shadow Angle</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.shadow.shadowAngle}
                            onChange={(e) =>
                              updateBackButtonShadow(
                                "shadowAngle",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Shadow Size</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.shadow.shadowSize}
                            onChange={(e) =>
                              updateBackButtonShadow(
                                "shadowSize",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Shadow Distance</label>
                          <input
                            type="number"
                            className="form-control"
                            value={settings.backButton.shadow.shadowDistance}
                            onChange={(e) =>
                              updateBackButtonShadow(
                                "shadowDistance",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Shadow Color</label>
                          <MuiColorInput
                            format="hex"
                            value={settings.backButton.shadow.shadowColor}
                            fullWidth
                            onChange={(color) =>
                              updateBackButtonShadow("shadowColor", color)
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "0px",
                                height: 53,
                              },
                              "& .MuiOutlinedInput-input": {
                                height: "36px",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row menu-card2">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Shadow Alpha</label>
                          <input
                            type="number"
                            className="form-control"
                            step="0.1"
                            min="0"
                            max="1"
                            value={settings.backButton.shadow.shadowAlpha}
                            onChange={(e) =>
                              updateBackButtonShadow(
                                "shadowAlpha",
                                parseFloat(e.target.value),
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );

  // ==================== QR CODE & MAP POSITION TAB ====================
  const renderQRCodeMapTab = () => (
    <Box>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="dashboard-container">
            {/* QR Code Settings Card */}
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  QR Code Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Configure the global size and position of QR codes on the
                  Avatar Screen. Use the language selector in the page header to edit text for different languages.
                </Typography>

                <Grid container spacing={3}>
                  {/* QR Code Width */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>QR Code Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.qrCode.widthPx || 403}
                        onChange={(e) =>
                          updateQRCode("widthPx", parseFloat(e.target.value))
                        }
                        min="200"
                        max="600"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Range: 200px - 600px (responsive sizing applied)
                      </small>
                    </div>
                  </Grid>

                  {/* QR Code Height */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>QR Code Height (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.qrCode.heightPx || 326}
                        onChange={(e) =>
                          updateQRCode("heightPx", parseFloat(e.target.value))
                        }
                        min="200"
                        max="600"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Range: 200px - 600px (responsive sizing applied)
                      </small>
                    </div>
                  </Grid>

                  {/* Top Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Top Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.qrCode.top === "number"
                            ? settings.qrCode.top
                            : ""
                        }
                        onChange={(e) =>
                          updateQRCode(
                            "top",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="50"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to unset. Range: 0% - 50%
                      </small>
                    </div>
                  </Grid>

                  {/* Right Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Right Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.qrCode.right === "number"
                            ? settings.qrCode.right
                            : ""
                        }
                        onChange={(e) =>
                          updateQRCode(
                            "right",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="20"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to unset. Range: 0% - 20%
                      </small>
                    </div>
                  </Grid>

                  {/* Left Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Left Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.qrCode.left === "number"
                            ? settings.qrCode.left
                            : ""
                        }
                        onChange={(e) =>
                          updateQRCode(
                            "left",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="20"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to use Right Offset. Range: 0% - 20%
                      </small>
                    </div>
                  </Grid>

                  {/* Bottom Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Bottom Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.qrCode.bottom === "number"
                            ? settings.qrCode.bottom
                            : ""
                        }
                        onChange={(e) =>
                          updateQRCode(
                            "bottom",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="50"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to use Top Offset. Range: 0% - 50%
                      </small>
                    </div>
                  </Grid>

                  {/* Background Color */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Background Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.qrCode.backgroundColor || "#127E10"}
                        onChange={(color) =>
                          updateQRCode("backgroundColor", color)
                        }
                        disabled={settings.qrCode.backgroundTransparent}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                      <small className="form-text text-muted">
                        Background color of the QR code container
                      </small>
                    </div>
                  </Grid>

                  {/* Background Transparent Checkbox */}
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.qrCode.backgroundTransparent ?? false}
                          onChange={(e) =>
                            updateQRCode("backgroundTransparent", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Transparent Background"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      When enabled, the QR code container background will be transparent instead of using the background color.
                    </Typography>
                  </Grid>

                  {/* Border Width */}
                  <Grid item xs={12} md={4}>
                    <div className="form-group">
                      <label>Border Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.qrCode.borderWidth || "1"}
                        onChange={(e) =>
                          updateQRCode("borderWidth", e.target.value)
                        }
                        min="0"
                        max="20"
                        step="1"
                      />                 
                    </div>
                  </Grid>

                  {/* Border Color */}
                  <Grid item xs={12} md={4}>
                    <div className="form-group">
                      <label>Border Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.qrCode.borderColor || "#127E10"}
                        onChange={(color) =>
                          updateQRCode("borderColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                      <small className="form-text text-muted">
                        Color of the QR code frame border
                      </small>
                    </div>
                  </Grid>

                  {/* Border Radius */}
                  <Grid item xs={12} md={4}>
                    <div className="form-group">
                      <label>Border Radius (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.qrCode.borderRadius || "20"}
                        onChange={(e) =>
                          updateQRCode("borderRadius", e.target.value)
                        }
                        min="0"
                        max="50"
                        step="1"
                      />
                      
                    </div>
                  </Grid>

                  {/* Header Text */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>
                        Header Text
                        {availableLanguages.length > 1 && (
                          <Chip
                            label={`Editing: ${getLanguageName(currentLanguage)}`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: buttonColor,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={settings.qrCode.headerText || "Scan Me"}
                        onChange={(e) =>
                          updateQRCode("headerText", e.target.value)
                        }
                        placeholder="Scan Me"
                      />
                      <small className="form-text text-muted">
                        Text displayed above the QR code
                      </small>
                    </div>
                  </Grid>

                  {/* Header Font Size */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Header Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.qrCode.headerFontSize || 14}
                        onChange={(e) =>
                          updateQRCode(
                            "headerFontSize",
                            parseFloat(e.target.value),
                          )
                        }
                        min="8"
                        max="32"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Range: 8px - 32px
                      </small>
                    </div>
                  </Grid>

                  {/* Header Color */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Header Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.qrCode.headerColor || "#127E10"}
                        onChange={(color) => updateQRCode("headerColor", color)}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                      <small className="form-text text-muted">
                        Color of the header text
                      </small>
                    </div>
                  </Grid>

                  {/* Footer Text */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>
                        Footer Text
                        {availableLanguages.length > 1 && (
                          <Chip
                            label={`Editing: ${getLanguageName(currentLanguage)}`}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: buttonColor,
                              color: 'white',
                              fontSize: '0.7rem',
                              height: '20px',
                            }}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          settings.qrCode.footerText ||
                          "Get directions on your phone"
                        }
                        onChange={(e) =>
                          updateQRCode("footerText", e.target.value)
                        }
                        placeholder="Get directions on your phone"
                      />
                      <small className="form-text text-muted">
                        Text displayed below the QR code
                      </small>
                    </div>
                  </Grid>

                  {/* Footer Font Size */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Footer Font Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.qrCode.footerFontSize || 10}
                        onChange={(e) =>
                          updateQRCode(
                            "footerFontSize",
                            parseFloat(e.target.value),
                          )
                        }
                        min="6"
                        max="24"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Range: 6px - 24px
                      </small>
                    </div>
                  </Grid>

                  {/* Footer Color */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Footer Text Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.qrCode.footerColor || "#666666"}
                        onChange={(color) => updateQRCode("footerColor", color)}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                      <small className="form-text text-muted">
                        Color of the footer text
                      </small>
                    </div>
                  </Grid>

                  {/* Show Frame Toggle */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.qrCode.showFrame ?? true}
                          onChange={(e) =>
                            updateQRCode("showFrame", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Show QR Code Frame"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      When enabled, displays QR code with frame styling (background color, padding, border). 
                      When disabled, displays only the transparent QR code image without any frame.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Card>

            {/* Global QR Code Generator Card */}
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  QR Code Configuration
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Configure global QR code settings that will be applied to all new locations automatically.
                </Typography>

                {/* QR Code Configuration Settings */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={qrCodeConfig.enabled}
                            onChange={(e) => handleQrConfigChange('enabled', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Enable QR Code Generation"
                      />
                      <small className="form-text text-muted">
                        Enable or disable QR code functionality globally
                      </small>
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={qrCodeConfig.autoGenerate}
                            onChange={(e) => handleQrConfigChange('autoGenerate', e.target.checked)}
                            color="primary"
                            disabled={!qrCodeConfig.enabled}
                          />
                        }
                        label="Auto-Generate QR Codes for New Locations"
                      />
                      <small className="form-text text-muted">
                        Automatically generate QR codes when creating new locations
                      </small>
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={qrCodeConfig.enableDirectMapLinkQr}
                            onChange={(e) => handleQrConfigChange('enableDirectMapLinkQr', e.target.checked)}
                            color="primary"
                            disabled={!qrCodeConfig.enabled}
                          />
                        }
                        label="Enable Direct Map Link QR Codes"
                      />
                      <small className="form-text text-muted">
                        When enabled, QR codes will link directly to MappedIn web interface instead of the custom wayfinder
                      </small>
                    </div>
                  </Grid>
                </Grid>

                {/* Save QR Configuration Button */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={saveQRCodeConfig}
                    sx={{
                      backgroundColor: buttonColor,
                      '&:hover': {
                        backgroundColor: buttonColor,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Save QR Configuration
                  </Button>
                </Box>
              </Box>
            </Card>

            {/* Global QR Code Generator Card */}
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  Global QR Code Generator
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Generate a single QR code design that will be used for all locations. 
                  Individual locations can enable/disable QR codes in their settings.
                </Typography>
 

                {/* Current QR Code Display */}
                {settings.qrCode?.generatedQrUrl && (
                  <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <h4>Current Global QR Code</h4>
                    <img 
                      src={settings.qrCode.generatedQrUrl} 
                      alt="Global QR Code" 
                      style={{ width: '200px', height: '200px', border: '1px solid #ddd', borderRadius: '8px' }}
                    />
                  </div>
                )}

                {/* Pattern Selection */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <h3>Pick a Color & Shape for Your QR Code</h3>
                  <div
                    style={{
                      display: 'flex',
                      gap: '20px',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      marginTop: '20px',
                      marginBottom: '20px',
                    }}
                  >
                    {[
                      {
                        id: 1,
                        src: 'https://dashboard.uniqode.com/assets/logo/qr-customize/Frame.svg',
                        alt: 'Frames',
                        type: 'frame',
                      },
                      {
                        id: 2,
                        src: 'https://dashboard.uniqode.com/assets/logo/qr-customize/Logo.svg',
                        alt: 'Logo & Background',
                        type: 'logo',
                      },
                      {
                        id: 3,
                        src: 'https://dashboard.uniqode.com/assets/logo/qr-customize/Eye.svg',
                        alt: 'Eyes',
                        type: 'eyes',
                      },
                      {
                        id: 4,
                        src: 'https://dashboard.uniqode.com/assets/logo/qr-customize/Pattern.svg',
                        alt: 'Patterns',
                        type: 'pattern',
                      },
                    ].map(pattern => (
                      <div key={pattern.id} style={{ textAlign: 'center' }}>
                        <div
                          onClick={() => setSelectedQrPattern(pattern.id)}
                          style={{
                            border: `3px solid ${selectedQrPattern === pattern.id ? '#4caf50' : 'transparent'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'border-color 0.3s',
                            padding: '5px',
                            boxShadow: selectedQrPattern === pattern.id ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
                          }}
                        >
                          <img
                            src={pattern.src}
                            alt={pattern.alt}
                            style={{
                              width: '70px',
                              objectFit: 'cover',
                              borderRadius: '5px',
                            }}
                          />
                          <p style={{ textTransform: 'capitalize', marginTop: '5px' }}>
                            {pattern.alt}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* QR Settings based on selected pattern */}
                {renderQrPatternSettings()}

                {/* Generate Buttons */}
                <div className="row" style={{ marginTop: '30px' }}>
                  <div className="col-md-6" style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={generateGlobalQRCode}
                      style={{
                        backgroundColor: qrGenerating ? '#cccccc' : buttonColor,
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '15px 30px',
                        fontSize: '16px',
                        cursor: qrGenerating ? 'not-allowed' : 'pointer',
                        width: '100%',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                      }}
                      disabled={qrGenerating}
                    >
                      {qrGenerating && (
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: '20px',
                            height: '20px',
                            border: '3px solid #ffffff',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' },
                            },
                          }}
                        />
                      )}
                      {qrGenerating ? 'Generating QR Codes...' : 'Generate All Location QR Codes'}
                    </button>
                    
                    {qrGenerating && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                        {/* Progress Bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <Box
                              sx={{
                                height: '8px',
                                borderRadius: '4px',
                                backgroundColor: '#e0e0e0',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${qrGenerationProgress}%`,
                                  backgroundColor: buttonColor || '#1976d2',
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ minWidth: 50 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {qrGenerationProgress}%
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Status Message */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {qrGenerationMessage}
                        </Typography>
                        
                        {/* Warning */}
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 500 }}>
                          ⚠️ Do not close this page or navigate away
                        </Typography>
                      </Box>
                    )}
                  </div>
                  
                  {/* Generate Direct Map Link QR Codes Button */}
                  <div className="col-md-6" style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={handleGenerateBulkDirectMapQR}
                      style={{
                        backgroundColor: (generatingDirectMapQR || !qrCodeConfig.enabled) ? '#cccccc' : '#ff5722',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '15px 30px',
                        fontSize: '16px',
                        cursor: (generatingDirectMapQR || !qrCodeConfig.enabled) ? 'not-allowed' : 'pointer',
                        width: '100%',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                      }}
                      disabled={generatingDirectMapQR || !qrCodeConfig.enabled}
                    >
                      {generatingDirectMapQR && (
                        <Box
                          sx={{
                            display: 'inline-block',
                            width: '20px',
                            height: '20px',
                            border: '3px solid #ffffff',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' },
                            },
                          }}
                        />
                      )}
                      {generatingDirectMapQR ? `Generating... ${directMapQRProgress}%` : 'Generate Direct Map Link QR Codes (Bulk)'}
                    </button>
                    
                    {generatingDirectMapQR && directMapQRProgress > 0 && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                        {/* Progress Bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <Box
                              sx={{
                                height: '8px',
                                borderRadius: '4px',
                                backgroundColor: '#e0e0e0',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${directMapQRProgress}%`,
                                  backgroundColor: '#ff5722',
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ minWidth: 50 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {directMapQRProgress}%
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Status Message */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {directMapQRStatus}
                        </Typography>
                        
                        {/* Warning */}
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 500 }}>
                          ⚠️ Do not close this page or navigate away
                        </Typography>
                      </Box>
                    )}
                  </div>
                </div>
              </Box>
            </Card>

            {/* Map Position Settings Card */}
            <Card>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  Map Position & Size Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Configure the global position and size of the map overlay on
                  the Avatar Screen
                </Typography>

                <Grid container spacing={3}>
                  {/* Map Width */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Map Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.mapPosition.widthPx || 537}
                        onChange={(e) =>
                          updateMapPosition(
                            "widthPx",
                            parseFloat(e.target.value),
                          )
                        }
                        min="192"
                        max="960"
                        step="1"
                      />
                     
                    </div>
                  </Grid>

                  {/* Map Height */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Map Height (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.mapPosition.heightPx || 1080}
                        onChange={(e) =>
                          updateMapPosition(
                            "heightPx",
                            parseFloat(e.target.value),
                          )
                        }
                        min="324"
                        max="972"
                        step="1"
                      />
                     
                    </div>
                  </Grid>

                  {/* Top Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Top Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.mapPosition.top === "number"
                            ? settings.mapPosition.top
                            : ""
                        }
                        onChange={(e) =>
                          updateMapPosition(
                            "top",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="50"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to unset. Range: 0% - 50%
                      </small>
                    </div>
                  </Grid>

                  {/* Right Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Right Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.mapPosition.right === "number"
                            ? settings.mapPosition.right
                            : ""
                        }
                        onChange={(e) =>
                          updateMapPosition(
                            "right",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="20"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to unset. Range: 0% - 20%
                      </small>
                    </div>
                  </Grid>

                  {/* Left Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Left Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.mapPosition.left === "number"
                            ? settings.mapPosition.left
                            : ""
                        }
                        onChange={(e) =>
                          updateMapPosition(
                            "left",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="20"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to use Right Offset. Range: 0% - 20%
                      </small>
                    </div>
                  </Grid>

                  {/* Bottom Offset */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Bottom Offset (Optional) (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          typeof settings.mapPosition.bottom === "number"
                            ? settings.mapPosition.bottom
                            : ""
                        }
                        onChange={(e) =>
                          updateMapPosition(
                            "bottom",
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="50"
                        step="0.5"
                        placeholder="Not Set"
                      />
                      <small className="form-text text-muted">
                        Leave empty to use Top Offset. Range: 0% - 50%
                      </small>
                    </div>
                  </Grid>

                  {/* Map Styling Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      Map Styling
                    </Typography>
                  </Grid>

                  {/* Border Color */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Border Color</label>
                      <MuiColorInput
                        format="hex"
                        value={settings.mapPosition.borderColor || "#000000"}
                        onChange={(color) =>
                          updateMapPosition("borderColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                      <small className="form-text text-muted">
                        Color of the map container border
                      </small>
                    </div>
                  </Grid>

                  {/* Border Width */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Border Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.mapPosition.borderWidth || "1"}
                        onChange={(e) =>
                          updateMapPosition("borderWidth", e.target.value)
                        }
                        min="0"
                        max="10"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Width of the map container border (0-10px)
                      </small>
                    </div>
                  </Grid>

                  {/* Border Radius */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Border Radius (vw)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.mapPosition.borderRadius || "8"}
                        onChange={(e) =>
                          updateMapPosition("borderRadius", e.target.value)
                        }
                        min="0"
                        max="50"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Corner radius in vw units (0-50vw)
                      </small>
                    </div>
                  </Grid>

                  {/* Box Shadow Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      Box Shadow
                    </Typography>
                  </Grid>

                  {/* Shadow Angle */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Shadow Angle (degrees)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.mapPosition.boxShadow?.shadowAngle || 45
                        }
                        onChange={(e) =>
                          updateMapPositionShadow(
                            "shadowAngle",
                            parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="360"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Angle of the shadow (0-360 degrees)
                      </small>
                    </div>
                  </Grid>

                  {/* Shadow Size */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Shadow Size (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={settings.mapPosition.boxShadow?.shadowSize || 5}
                        onChange={(e) =>
                          updateMapPositionShadow(
                            "shadowSize",
                            parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="50"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Size of the shadow blur (0-50px)
                      </small>
                    </div>
                  </Grid>

                  {/* Shadow Distance */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Shadow Distance (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.mapPosition.boxShadow?.shadowDistance || 2
                        }
                        onChange={(e) =>
                          updateMapPositionShadow(
                            "shadowDistance",
                            parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="50"
                        step="1"
                      />
                      <small className="form-text text-muted">
                        Distance of the shadow offset (0-50px)
                      </small>
                    </div>
                  </Grid>

                  {/* Shadow Opacity */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Shadow Opacity</label>
                      <input
                        type="number"
                        className="form-control"
                        value={
                          settings.mapPosition.boxShadow?.shadowAlpha || 0.3
                        }
                        onChange={(e) =>
                          updateMapPositionShadow(
                            "shadowAlpha",
                            parseFloat(e.target.value),
                          )
                        }
                        min="0"
                        max="1"
                        step="0.1"
                      />
                      <small className="form-text text-muted">
                        Opacity of the shadow (0-1)
                      </small>
                    </div>
                  </Grid>

                  {/* Shadow Color */}
                  <Grid item xs={12} md={6}>
                    <div className="form-group">
                      <label>Shadow Color</label>
                      <MuiColorInput
                        format="hex"
                        value={
                          settings.mapPosition.boxShadow?.shadowColor ||
                          "#000000"
                        }
                        onChange={(color) =>
                          updateMapPositionShadow("shadowColor", color)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0px",
                            height: 53,
                          },
                          "& .MuiOutlinedInput-input": {
                            height: "36px",
                          },
                        }}
                      />
                      <small className="form-text text-muted">
                        Color of the shadow
                      </small>
                    </div>
                  </Grid>
                </Grid>
              </Box>
            </Card>

            {/* Logo Before Map Settings Card */}
            <Card sx={{ mt: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                  Logo Before Map Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Configure a logo to display before the map loads globally across all locations
                </Typography>

                <div className="col-md-8 d-flex p-3">
                  <div
                    className={`form-group menu-card2 p-3 ${!settings.logoBeforeMap?.enabled ? "disabled" : ""} h-100 w-100`}
                  >
                    <div className="form-group">
                      <label>Logo Before Map:</label>
                      <ToggleSwitch
                        label="Enable"
                        isOn={settings.logoBeforeMap?.enabled || false}
                        id={"logoBeforeMapGlobal"}
                        onChange={(val) => updateLogoBeforeMap("enabled", val)}
                      />
                    </div>

                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: "#666", fontSize: "0.875rem" }}
                    >
                      Upload a logo to display before the map loads globally
                    </Typography>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "5px",
                        marginTop: "15px",
                      }}
                    >
                      <div
                        style={{
                          border: "2px dashed #cccccc",
                          padding: "20px",
                          borderRadius: "5px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => document.getElementById('logoBeforeMapGlobalInput').click()}
                      >
                        <input
                          id="logoBeforeMapGlobalInput"
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              setSelectedLogoBeforeMapFile(e.target.files[0]);
                            }
                          }}
                        />
                        {selectedLogoBeforeMapFile ? (
                          <p>{selectedLogoBeforeMapFile.name}</p>
                        ) : (
                          <p>Drag and drop a file here, or click to select a file</p>
                        )}
                      </div>
                      
                      {/* Uploaded Image Preview */}
                      <div style={{ margin: "auto" }}>
                        {settings.logoBeforeMap?.fileUrl ? (
                          <a href={settings.logoBeforeMap.fileUrl} target="_blank" rel="noreferrer">
                            <img
                              src={settings.logoBeforeMap.fileUrl}
                              style={{ maxWidth: "210px", maxHeight: "80px" }}
                              alt="Logo Before Map"
                            />
                          </a>
                        ) : null}
                      </div>
                    </div>

                    {/* Uploaded File Info */}
                    {settings.logoBeforeMap?.fileUrl && (
                      <div
                        style={{
                          marginTop: "20px",
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            marginTop: "15px",
                          }}
                        >
                          <div>
                            Uploaded File:{" "}
                            <a
                              href={settings.logoBeforeMap.fileUrl}
                              style={{ fontWeight: "bold" }}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {settings.logoBeforeMap.fileName}
                            </a>
                          </div>

                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleRemoveLogoBeforeMap}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "10px",
                        alignItems: "center",
                        marginTop: "20px",
                      }}
                    >
                      <button
                        onClick={() => {
                          if (selectedLogoBeforeMapFile) {
                            handleLogoBeforeMapUpload(selectedLogoBeforeMapFile);
                          }
                        }}
                        type="button"
                        style={{
                          marginTop: "20px",
                          backgroundColor: buttonColor,
                          border: "none",
                          borderRadius: "5px",
                          cursor: "pointer",
                          padding: "10px 20px",
                          color: "#FFFFFF",
                          margin: "0",
                        }}
                        disabled={!selectedLogoBeforeMapFile || uploading}
                      >
                        {uploading ? "Uploading..." : "Upload"}
                      </button>

                      {/* Logo Settings */}
                      {settings.logoBeforeMap?.fileUrl && (
                        <>
                          <div
                            className="dimensions"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: "15px",
                              height: "100%",
                            }}
                          >
                            {/* Size Input */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <label
                                style={{
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                  fontSize: "14px",
                                }}
                              >
                                Size (%):
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                style={{ width: "80px" }}
                                value={settings.logoBeforeMap?.width ?? 100}
                                onChange={(e) => {
                                  const width = parseInt(e.target.value) || 100;
                                  updateLogoBeforeMap("width", width);
                                }}
                                min="0"
                                max="100"
                                placeholder="Size"
                              />
                            </div>

                            {/* Display Duration Input */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <label
                                style={{
                                  fontWeight: "600",
                                  whiteSpace: "nowrap",
                                  fontSize: "14px",
                                }}
                              >
                                Duration (s):
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                style={{ width: "80px" }}
                                value={settings.logoBeforeMap?.displayDuration ?? 3}
                                onChange={(e) => {
                                  const duration = parseInt(e.target.value) || 3;
                                  updateLogoBeforeMap("displayDuration", Math.min(Math.max(duration, 0), 100));
                                }}
                                min="1"
                                max="10"
                                placeholder="Duration"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Box>
            </Card>
          </div>
        </div>
      </div>
    </Box>
  );

  // ==================== STEP-BY-STEP DIRECTIONS TAB ====================
  const renderStepByStepDirectionsTab = () => (
    <Box>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="dashboard-container">
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                  Global Step-by-Step Directions Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure the default step-by-step directions template that will be used across all categories, subcategories, and locations. 
                  Individual items can override these settings if needed.
                </Typography>

                <StepByStepDirectionsConfig
                  config={stepByStepConfig}
                  onChange={setStepByStepConfig}
                  showUseGlobal={false}
                  showUseParent={false}
                  buttonColor={buttonColor}
                  disabled={false}
                />

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-primary"
                    onClick={saveStepByStepConfig}
                    style={{
                      backgroundColor: buttonColor,
                      borderColor: buttonColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Save />
                    Save Step-by-Step Configuration
                  </button>
                </Box>
              </Box>
            </Card>
          </div>
        </div>
      </div>
    </Box>
  );

  // ==================== AI ENHANCEMENT TAB ====================
  const renderAIEnhancementTab = () => (
    <Box>
      <div className="menu-card2" style={{ padding: "20px", marginTop: "20px" }}>
        <EnhancedTextDirectionConfig
          userId={userId}
          buttonColor={buttonColor}
          showToast={(message, type) => {
            if (type === 'success') {
              showMsgSuccessToast(message);
            } else {
              showErrorToast(message);
            }
          }}
        />
      </div>
    </Box>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 0:
        return renderSearchBoxTab();
      case 1:
        return renderTimeoutPopupTab();
      case 2:
        return renderBackButtonTab();
      case 3:
        return renderQRCodeMapTab();
      case 4:
        return renderAIEnhancementTab();
      default:
        return renderSearchBoxTab();
    }
  };

  return (
    <div>
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="dashboard-container">
            <div className="tab-wizard wizard-circle wizard vertical">
              <div className="form-group menu-card2">
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      borderBottom: "1px solid #e0e0e0",
                      gap: "10px",
                    }}
                  >
                    <button
                      type="button"
                      className={`btn ${activeTab === 0 ? "active" : ""}`}
                      onClick={() => setActiveTab(0)}
                      style={{
                        backgroundColor:
                          activeTab === 0 ? buttonColor : "transparent",
                        color: activeTab === 0 ? "white" : "#212529",
                        border: "none",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <SearchIcon />
                      Search Box
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === 1 ? "active" : ""}`}
                      onClick={() => setActiveTab(1)}
                      style={{
                        backgroundColor:
                          activeTab === 1 ? buttonColor : "transparent",
                        color: activeTab === 1 ? "white" : "#212529",
                        border: "none",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <AccessTime />
                      Timeout Popup
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === 2 ? "active" : ""}`}
                      onClick={() => setActiveTab(2)}
                      style={{
                        backgroundColor:
                          activeTab === 2 ? buttonColor : "transparent",
                        color: activeTab === 2 ? "white" : "#212529",
                        border: "none",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <ArrowBack />
                      Back Button
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === 3 ? "active" : ""}`}
                      onClick={() => setActiveTab(3)}
                      style={{
                        backgroundColor:
                          activeTab === 3 ? buttonColor : "transparent",
                        color: activeTab === 3 ? "white" : "#212529",
                        border: "none",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <Settings />
                      QR Code & Map
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === 4 ? "active" : ""}`}
                      onClick={() => setActiveTab(4)}
                      style={{
                        backgroundColor:
                          activeTab === 4 ? buttonColor : "transparent",
                        color: activeTab === 4 ? "white" : "#212529",
                        border: "none",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderRadius: "4px 4px 0 0",
                      }}
                    >
                      <Psychology />
                      AI Enhancement
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="tab-content" style={{ padding: "20px 0" }}>
                  {isLoading ? (
                    <div className="text-center">
                      <Typography>Loading settings...</Typography>
                    </div>
                  ) : (
                    renderActiveTab()
                  )}
                </div>

                <div className="page-header">
                  <div className="row">
                    <div
                      className="col-md-12 col-sm-12 text-end "
                      style={{ display: "flex", justifyContent: "end" }}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isLoading}
                        style={{
                          backgroundColor: buttonColor,
                          borderColor: buttonColor,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {isLoading ? "Saving..." : "Save All Settings"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <PreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        title={`${getPreviewTitle()} Preview`}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
          }}
        >
          {renderPreviewDevice(renderPreviewContent(), true)}
        </div>
      </PreviewModal>
    </div>
  );
};

const getFileType = (fileUrl) => {
  if (!fileUrl) return "blank"; // No URL provided

  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg"];
  const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];

  if (imageExtensions.some((ext) => fileUrl.toLowerCase().endsWith(ext))) {
    return "image";
  }
  if (videoExtensions.some((ext) => fileUrl.toLowerCase().endsWith(ext))) {
    return "video";
  }
  return "other"; // Other file types or unsupported extensions
};

const MediaDropzone = ({ onDrop, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "video/*": [".mp4", ".mov", ".mp3"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      style={{
        border: "2px dashed #cccccc",
        padding: "20px",
        borderRadius: "5px",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <p>{selectedFile.name}</p>
      ) : (
        <p>Drag and drop a file here, or click to select a file</p>
      )}
    </div>
  );
};

const UploadButton = ({ uploading, onUpload, buttonColor, ...props }) => (
  <button
    onClick={onUpload}
    type="button"
    style={{
      marginTop: "20px",
      //   padding: "10px 20px",
      backgroundColor: buttonColor,
      // color: "#FFFFFF",
      border: "none",
      borderRadius: "5px",
      cursor: "pointer",
      ...props.style,
    }}
    disabled={uploading}
  >
    {uploading ? "Uploading..." : "Upload"}
  </button>
);

// UploadedUrl Component
const UploadedUrl = ({ url, uploadedUrls, handleDeleteLogo, logoState }) =>
  url ? (
    <div
      style={{
        marginTop: "20px",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start", // Adjust alignment of child elements (optional)
        gap: "10px", // Add spacing between child elements (optional)
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginTop: "15px",
        }}
      >
        <div>
          Uploaded File:{" "}
          <a
            href={url.fileUrl}
            style={{ fontWeight: "bold" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {url.fileName}
          </a>
        </div>

        {uploadedUrls.fileUrl && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDeleteLogo(logoState)}
          >
            <Delete />
          </Button>
        )}
      </div>
      {/* <div style={{ float: 'right', width: '30%', textAlign: 'right' }}>
        {getFileType(url.fileUrl) === 'image' ? (
          // Render image if it's an image file
          <a href={url.fileUrl} target="_blank" rel="noreferrer">
            <img src={url.fileUrl} style={{ maxWidth: '200px', maxHeight: '60px' }} alt="" />
          </a>
        ) : getFileType(url.fileUrl) === 'video' ? (
          // Render play button for video file
          <a href={url.fileUrl} target="_blank" rel="noreferrer">
            <img
              src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
              style={{ maxWidth: '200px', maxHeight: '60px' }}
              alt="Play Video"
            />
          </a>
        ) : (
          // Fallback for other cases
          ''
        )}
      </div> */}
    </div>
  ) : null;

const UploadedUrlImage = ({ url }) => {
  return (
    <>
      {url ? (
        <div style={{ margin: "auto" }}>
          {getFileType(url.fileUrl) === "image" ? (
            // Render image if it's an image file
            <a href={url.fileUrl} target="_blank" rel="noreferrer">
              <img
                src={url.fileUrl}
                style={{ maxWidth: "210px", maxHeight: "80px" }}
                alt=""
              />
            </a>
          ) : getFileType(url.fileUrl) === "video" ? (
            // Render play button for video file
            <a href={url.fileUrl} target="_blank" rel="noreferrer">
              <img
                src="https://paulavatar.s3.us-east-2.amazonaws.com/play-button.png"
                style={{ maxWidth: "200px", maxHeight: "60px" }}
                alt="Play Video"
              />
            </a>
          ) : (
            // Fallback for other cases
            ""
          )}
        </div>
      ) : null}
    </>
  );
};

export default BrandingSettings;
